// src/components/Replan/Replan.js
import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react'; // Ensure useState and useEffect are imported
import { createPortal } from 'react-dom';
import Konva from 'konva';
import vcontrolAPI from '../../services/documentApi';
import {
  Stage,
  Layer,
  Group,
  Image as KonvaImage,
  Rect as KonvaRect,
  Text as KonvaText,
  Circle,
  Line as KonvaLine,
} from 'react-konva';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VCONTROL } from '../../constants/routes';

// Hooks
import { useComponentSize } from './hooks/useComponentSize';
import { useImageLoader } from './hooks/useImageLoader';
import { usePdfStreaming } from './hooks/usePdfStreaming';
import * as pdfjsLib from 'pdfjs-dist';
import { useDrawing } from './hooks/useDrawing';

// Configure PDF.js worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.mjs`;
import { useStageInteraction } from './hooks/useStageInteraction';
import { useUrlSync } from './hooks/useUrlSync';
import { useInteractiveTransform } from './hooks/useInteractiveTransform';

// Components
import ShapeRenderer, {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_AREA,
  DISPLAY_KEY_MEASUREMENT,
} from './shapes/ShapeRenderer';
import LockedScaleOverlay from './shapes/LockedScaleOverlay';
import TextEditor from './components/TextEditor';
import Grid from './components/Grid';
import SegmentMeasurementOverlay from './shapes/SegmentMeasurementOverlay';

// Components for toolbars and controls
import Toolbar from './components/Toolbar';
import PdfControls from './components/PdfControls';
import ShapeList from '../ShapeList/ShapeList.js';
import ContextualToolPanel from './components/ContextualToolPanel.js';
import DisplayKeysPanel from './components/DisplayKeysPanel.js';
import HoverThumbnailPreview from './components/HoverThumbnailPreview';
// Lazy: the upload workflow (tesseract.js, pdf.js, VControl.css, ROI canvas)
// must not be part of Replan's initial bundle. It is fetched only when the
// "Upload a plan" modal is opened.
const UploadPlansModal = React.lazy(() => import('./components/UploadPlansModal'));

// Constants & Utils
import { TOOLS } from './constants/drawingTools';
import {
  DEFAULT_METRIC_SCALE_KEY,
  DEFAULT_IMPERIAL_SCALE_KEY,
  METRIC_SCALES,
  IMPERIAL_SCALES
} from './constants/scales'; // Import scale constants
import { withFirebase } from '../Firebase';
import {
  AuthUserContext,
  withAuthorization,
  withEmailVerification,
} from '../Session';
import { useProjectSelection } from '../ProjectSelection';
import './Replan.css'; // Optional: If you create a CSS module

import {
  enhanceShapeWithMeasurements,
  cleanUndefined,
  generateId,
  deduplicateShapes,
  recalculateShapeMeasurements,
  resolveShapeScaleProps,
  getDisplayMeasurement,
  getDisplayAreaMeasurement,
} from './utils/drawingUtils.js';
import {
  getShapesCenter,
} from './utils/transformationUtils';
import { throttle } from './utils/debounce';
import {
  getOrderedShapesFromPageData,
  sanitizeShapeForFirestore,
  areShapesEquivalentForSaveDiff,
  extractShapeDefaults,
  compactShape,
} from './utils/shapePersistence';

// Metadata fields persisted per shape for auditability.
const SHAPE_METADATA_FIELDS = [
  'createdByUsername',
  'createdAt',
  'updatedByUsername',
  'updatedAt',
];

const DRAFT_AUTOSAVE_DELAY_MS = 1000;

const getDefaultScaleKeyForMeasurementSystem = (
  measurementSystem,
) =>
  measurementSystem === 'imperial'
    ? DEFAULT_IMPERIAL_SCALE_KEY
    : DEFAULT_METRIC_SCALE_KEY;

const buildReplanDraftPreferencePath = (
  organization,
  projectName,
  version,
  pageIndex,
) => `replanDrafts/${organization}/${projectName}/${version}/${pageIndex}`;

const buildReplanDraftIndexPreferencePath = (
  organization,
  projectName,
  versionName,
  pageIndex,
) =>
  [
    'replanDraftIndex',
    organization,
    projectName,
    versionName,
    String(pageIndex),
  ].join('/');

const buildReplanDraftStateKey = (
  organization,
  projectName,
  versionName,
  pageIndex,
) =>
  [organization || '', projectName || '', versionName || '', String(pageIndex)].join('::');

const getDraftStateKeyFromContext = ({
  organization,
  projectName,
  versionName,
  pageIndex,
}) => {
  if (
    !organization ||
    !projectName ||
    !versionName ||
    typeof pageIndex !== 'number'
  ) {
    return null;
  }

  return buildReplanDraftStateKey(
    organization,
    projectName,
    versionName,
    pageIndex,
  );
};

const buildPageDraftPayload = ({
  shapes,
  currentShape,
  isDrawing,
  measurementSystem,
  selectedScaleKey,
  dpiForSave,
  displayUnitSystem,
}) => {
  const baseShapes = deduplicateShapes(shapes).filter((shape) =>
    Boolean(shape?.id) && !shape.sourceVersion,
  );

  const shapesById = {};
  const shapeOrder = [];

  baseShapes.forEach((shape) => {
    const normalizedShape =
      !shape.originalDPI && dpiForSave
        ? { ...shape, originalDPI: dpiForSave }
        : shape;

    shapesById[shape.id] = sanitizeShapeForFirestore(normalizedShape);
    shapeOrder.push(shape.id);
  });

  const normalizedCurrentShape =
    isDrawing && currentShape
      ? sanitizeShapeForFirestore(
        !currentShape.originalDPI && dpiForSave
          ? { ...currentShape, originalDPI: dpiForSave }
          : currentShape,
      )
      : null;

  return {
    measurementSystem,
    displayUnitSystem: displayUnitSystem || null,
    selectedScaleKey:
      selectedScaleKey ||
      getDefaultScaleKeyForMeasurementSystem(measurementSystem),
    shapesById,
    shapeOrder,
    currentShape: normalizedCurrentShape,
    isDrawing: Boolean(isDrawing && normalizedCurrentShape),
    updatedAt: Date.now(),
  };
};

const createComparablePageSnapshot = (pageData) => {
  const measurementSystem = pageData?.measurementSystem || 'metric';
  const selectedScaleKey =
    pageData?.selectedScaleKey ||
    getDefaultScaleKeyForMeasurementSystem(measurementSystem);

  return {
    measurementSystem,
    displayUnitSystem: pageData?.displayUnitSystem || null,
    selectedScaleKey,
    currentShape: pageData?.currentShape
      ? sanitizeShapeForFirestore(pageData.currentShape)
      : null,
    isDrawing: Boolean(pageData?.isDrawing && pageData?.currentShape),
    shapes: deduplicateShapes(
      getOrderedShapesFromPageData(pageData),
    )
      .filter((shape) => Boolean(shape?.id))
      .map((shape) => sanitizeShapeForFirestore(shape)),
  };
};

const arePageSnapshotsEquivalent = (left, right) => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  if (
    left.measurementSystem !== right.measurementSystem ||
    left.displayUnitSystem !== right.displayUnitSystem ||
    left.selectedScaleKey !== right.selectedScaleKey
  ) {
    return false;
  }

  if (left.shapes.length !== right.shapes.length) {
    return false;
  }

  if (Boolean(left.currentShape) !== Boolean(right.currentShape)) {
    return false;
  }

  if (
    left.currentShape &&
    right.currentShape &&
    !areShapesEquivalentForSaveDiff(
      left.currentShape,
      right.currentShape,
    )
  ) {
    return false;
  }

  if (Boolean(left.isDrawing) !== Boolean(right.isDrawing)) {
    return false;
  }

  for (let index = 0; index < left.shapes.length; index += 1) {
    const leftShape = left.shapes[index];
    const rightShape = right.shapes[index];

    if (
      leftShape?.id !== rightShape?.id ||
      !areShapesEquivalentForSaveDiff(leftShape, rightShape)
    ) {
      return false;
    }
  }

  return true;
};

const updateAssemblyMeasurements = async (
  firebase,
  org,
  project,
  version,
  pageIndex,
  baseShapes,
  persistedShapes,
  scaleKey,
) => {
  const ALL_SCALES = [...METRIC_SCALES, ...IMPERIAL_SCALES];
  const scaleProps = ALL_SCALES.find(s => s.key === scaleKey) || ALL_SCALES[0];
  const dpi = 125;
  const imageSize = { width: 1000, height: 1000 };
  const pageKey = `${version}|${pageIndex}`;

  // Normalize assembly IDs so that old names like "BL1 (Cloisons)"
  // aggregate into the same document as "BL1_Cloisons". Timestamp-style
  // legacy IDs pass through unchanged.
  const normalizeId = (id) =>
    firebase && typeof firebase.normalizeAssemblyId === 'function'
      ? firebase.normalizeAssemblyId(id) || id
      : id;

  const cur = {};
  for (const shape of baseShapes) {
    const rawId = shape.params?.Assembly;
    if (!rawId) continue;
    const id = normalizeId(rawId);
    if (!cur[id]) cur[id] = { totalMeasurement: 0, shapeCount: 0, isArea: false };
    cur[id].shapeCount += 1;
    const raw = shape.area || shape.totalDistance || 0;
    if (raw > 0) {
      cur[id].totalMeasurement += Math.abs(raw);
      if (shape.area) cur[id].isArea = true;
    }
  }

  const affected = new Set(Object.keys(cur));
  for (const shape of persistedShapes.values()) {
    if (shape.params?.Assembly) affected.add(normalizeId(shape.params.Assembly));
  }
  if (affected.size === 0) return;

  const collectionName = org || 'projects';
  const docRef = (id) =>
    firebase.fsdb.collection(collectionName).doc(project).collection('assemblies').doc(id);

  for (const id of affected) {
    const docSnap = await docRef(id).get();
    if (!docSnap.exists) {
      // Do not create a new assembly document from measurement writes.
      // Assemblies must be created explicitly via ShapeListItem or AssemblyManager.
      console.warn(
        `Skipping assembly measurement update: assembly "${id}" does not exist.`,
      );
      continue;
    }
    const existing = docSnap.data() || {};
    const existingPages = existing.pageMeasurements || {};

    let updatedPages;
    if (cur[id]) {
      let pageDisplayValue, pageUnit;
      try {
        if (cur[id].isArea) {
          const r = getDisplayAreaMeasurement(cur[id].totalMeasurement, scaleProps, dpi);
          pageDisplayValue = r.value; pageUnit = r.unit;
        } else {
          const r = getDisplayMeasurement(cur[id].totalMeasurement, scaleProps, dpi, imageSize);
          pageDisplayValue = r.value; pageUnit = r.unit;
        }
      } catch { pageDisplayValue = cur[id].totalMeasurement.toFixed(1); pageUnit = ''; }
      updatedPages = {
        ...existingPages,
        [pageKey]: { totalMeasurement: cur[id].totalMeasurement, shapeCount: cur[id].shapeCount, displayValue: pageDisplayValue, unit: pageUnit },
      };
    } else {
      updatedPages = { ...existingPages };
      delete updatedPages[pageKey];
    }

    let totalMeasurement = 0;
    let totalShapeCount = 0;
    for (const pm of Object.values(updatedPages)) {
      totalMeasurement += pm.totalMeasurement || 0;
      totalShapeCount += pm.shapeCount || 0;
    }

    if (totalShapeCount === 0 || Object.keys(updatedPages).length === 0) {
      await docRef(id).set({
        totalMeasurement: 0,
        totalDisplayValue: null,
        totalDisplayUnit: null,
        shapeCount: 0,
        isArea: false,
        pageMeasurements: null,
      }, { merge: true });
      continue;
    }

    const isArea = cur[id] ? cur[id].isArea : !!(existing.isArea);
    let displayValue, unit;
    try {
      if (isArea) {
        const r = getDisplayAreaMeasurement(totalMeasurement, scaleProps, dpi);
        displayValue = r.value; unit = r.unit;
      } else {
        const r = getDisplayMeasurement(totalMeasurement, scaleProps, dpi, imageSize);
        displayValue = r.value; unit = r.unit;
      }
    } catch {
      displayValue = totalMeasurement.toFixed(1);
      unit = '';
    }

    await docRef(id).set({
      totalMeasurement,
      totalDisplayValue: displayValue,
      totalDisplayUnit: unit,
      shapeCount: totalShapeCount,
      isArea,
      pageMeasurements: updatedPages,
    }, { merge: true });
  }
};

// Authorization condition
const condition = (authUser) => !!authUser;

// Blank-sheet geometry for the empty (no-plans) state: A0 portrait, world
// units = millimetres. Gives the stage real content to fit/pan/zoom like CAD.
const EMPTY_SHEET_WIDTH = 7354*.85;
const EMPTY_SHEET_HEIGHT = 5200*.85;

// Reads the theme-aware colors for the empty sheet. Konva needs concrete color
// strings, so we pull the Pico CSS variables ourselves and keep them in sync
// when the app theme toggles (see the MutationObserver in the component).
const readEmptySheetColors = () => {
  let ink = '#181c25';
  let accent = '#1095c1';
  let sheet = '#ffffff';
  let border = 'rgba(128,128,128,0.5)';
  try {
    const styles = getComputedStyle(document.documentElement);
    ink = styles.getPropertyValue('--pico-contrast').trim() || ink;
    accent = styles.getPropertyValue('--pico-primary').trim() || accent;
    sheet =
      styles.getPropertyValue('--pico-card-background-color').trim() || sheet;
    border =
      styles.getPropertyValue('--pico-muted-border-color').trim() || border;
  } catch (_e) {
    // fallbacks above
  }
  return { ink, accent, sheet, border };
};

const Replan = ({ firebase }) => {
  // Receive firebase as a prop
  // Get the authenticated user from the context
  const authUser = React.useContext(AuthUserContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const persistedShapesByIdRef = useRef(new Map());
  const publishedPageViewRef = useRef({
    enhancedShapes: [],
    currentShape: null,
    isDrawing: false,
    loadedMeasurementSystem: 'metric',
    loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
  });
  const publishedPageSnapshotRef = useRef(null);
  const privateDraftSnapshotRef = useRef(null);
  const hasLoadedCurrentPageRef = useRef(false);
  const loadedDraftPageKeyRef = useRef(null);
  const latestDraftStateRef = useRef(null);
  const draftStateByPageKeyRef = useRef(new Map());

  // --- Page Reference Hover Preview Logic States ---
  // Set Konva pixel ratio for high-DPI displays
  useEffect(() => {
    Konva.pixelRatio = window.devicePixelRatio || 1;
  }, []);

  const [previewData, setPreviewData] = useState(null); // { targetPageLabel, position }
  const [isHoveringPageRef, setIsHoveringPageRef] = useState(false);
  const hoverTimerRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0, clientX: 0, clientY: 0 });
  const draftAutosaveTimeoutRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const saveDraftIfNeededRef = useRef(async () => false);
  // MIGRATION-CLEANUP (remove after rollout, ~1-2 weeks)
  // Temporary flag: when a page is loaded from legacy `shapes` array format,
  // first save force-upserts all shapes into `shapesById + shapeOrder`.
  // Remove this ref together with the two blocks marked MIGRATION-CLEANUP below.
  const shouldMigrateLegacyShapesRef = useRef(false);
  // --- Refs ---
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const transformerRef = useRef(null);
  const overlayImageRef = useRef(null);
  const drawingLayerRef = useRef(null); // Keep if specific layer access needed
  // Tracks the timestamp of the last touchstart so that the browser-synthesized
  // mousedown/mousemove/mouseup that follows can be suppressed cross-browser.
  // (pointerType==='touch' is unreliable on iOS Safari.)
  const lastTouchTimeRef = useRef(0);

  // --- State ---
  const [mousePos, setMousePos] = useState({ x: null, y: null }); // State for mouse position
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [discardDraftStatus, setDiscardDraftStatus] = useState('idle');
  const [showDiscardDraft, setShowDiscardDraft] = useState(false);
  // const [isTouchPanning, setIsTouchPanning] = useState(false); // REMOVED: No longer needed

  // --- OCR Search State ---
  // searchMatches: Array of search results from PdfControls (position objects from OCR)
  // currentSearchIndex: Index of currently highlighted match for auto-centering
  const [searchMatches, setSearchMatches] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  // --- Interaction State for PDF Streaming ---
  const [isStageInteracting, setIsStageInteracting] = useState(false);
  const interactionTimeoutRef = useRef(null);

  // --- Experiment: No-Tax Fallback Logic ---
  const [isFallbackMode, setIsFallbackMode] = useState(false);

  const startInteraction = useCallback(() => {
    setIsStageInteracting(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
  }, []);

  const stopInteraction = useCallback(() => {
    // Small delay before stopping interaction to account for rapid event sequences (like scroll wheel)
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => {
      setIsStageInteracting(false);
    }, 80); // Short grace — just enough to coalesce rapid scroll events
  }, []);
  const {
    selectedProject: globalSelectedProject,
    setSelectedProject: setGlobalSelectedProject,
    projectsMeta,
    refreshProjects,
  } = useProjectSelection();
  const [projectName, setProjectName] = useState(
    globalSelectedProject || '',
  );
  // Tracks whether the saved-page restore has settled for the current project.
  // While false, useUrlSync must not write the page param to the URL: the
  // automatic first-page selection after a project switch would otherwise be
  // written as a phantom deep link and override the restored page.
  const [savedPageRestored, setSavedPageRestored] = useState(false);
  useEffect(() => {
    setSavedPageRestored(false);
  }, [projectName]);
  const handleSavedPageRestoreComplete = useCallback(() => {
    setSavedPageRestored(true);
  }, []);
  const [versionName, setVersionName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [pendingPageTarget, setPendingPageTarget] = useState(null);
  const [overlayTintColor, setOverlayTintColor] = useState('#ff7800');
  const [overlayModeEnabled, setOverlayModeEnabled] = useState(false);
  const [isOverlayOnTop, setIsOverlayOnTop] = useState(true);
  const [overlayPdfData, setOverlayPdfData] = useState(null);
  const [customScales, setCustomScales] = useState([]);
  const [overlayFilterEnabled, setOverlayFilterEnabled] = useState(true);

  // --- Multi-Version Shapes State ---
  const [selectedVersionShapes, setSelectedVersionShapes] = useState([]);
  useEffect(() => {
    const nextGlobalProject = globalSelectedProject || '';
    if (nextGlobalProject !== projectName) {
      setProjectName(nextGlobalProject);
    }
  }, [globalSelectedProject, projectName]);

  // Whether the selected project is known to have no uploaded documents.
  // Defaults to true when the projects list is still loading / the project is
  // unknown, so we never skip fetching based on stale or missing metadata.
  const projectHasDocuments =
    !projectName
      ? true
      : (projectsMeta[projectName]?.hasDocuments ?? true);

  // Self-healing: the projects list (and therefore `projectsMeta`) can be
  // stale — document uploads in VControl don't refresh it. When a project is
  // flagged as having no documents, trigger one refresh per project so a
  // recently uploaded plan is picked up and the editor loads normally.
  const refreshedNoDocsProjectRef = useRef(null);
  useEffect(() => {
    if (projectName && !projectHasDocuments) {
      if (refreshedNoDocsProjectRef.current !== projectName) {
        refreshedNoDocsProjectRef.current = projectName;
        refreshProjects();
      }
    } else {
      refreshedNoDocsProjectRef.current = null;
    }
  }, [projectName, projectHasDocuments, refreshProjects]);

  // Modal to upload a plan directly from the no-plans empty state. The shared
  // metadata knows which Firestore org the project belongs to (set during the
  // merge), so the workflow can publish under the right organisation.
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const uploadOrganisation = projectsMeta[projectName]?.organisation || '';

  // --- State for contextual tool panel ---
  const [contextualPanelPos, setContextualPanelPos] = useState(null);
  const [contextualPanelVisible, setContextualPanelVisible] =
    useState(false);

  // --- State for pages with shapes (for thumbnail badges) ---
  const [pagesWithShapes, setPagesWithShapes] = useState(new Map());

  // --- Text editing state ---
  const [textEditingState, setTextEditingState] = useState(null); // { shapeId, textRef }

  const [hoveredShapeId, setHoveredShapeId] = useState(null);
  const handleShapeHover = useCallback((shapeId, isHovering) => {
    setHoveredShapeId(prev => isHovering ? shapeId : (prev === shapeId ? null : prev));
  }, []);

  // --- Grouping state for ShapeList ---
  const [groupingKey, setGroupingKey] = useState(() => {
    return localStorage.getItem('replan_grouping_key') || '';
  });

  const [displayKeys, setDisplayKeys] = useState(() => {
    try {
      const stored = localStorage.getItem('replan_display_keys');
      return stored ? JSON.parse(stored) : [
        DISPLAY_KEY_SEGMENT_LENGTH,
        DISPLAY_KEY_AREA,
        DISPLAY_KEY_MEASUREMENT,
      ];
    } catch (e) {
      return [
        DISPLAY_KEY_SEGMENT_LENGTH,
        DISPLAY_KEY_AREA,
        DISPLAY_KEY_MEASUREMENT,
      ];
    }
  });

  const [displayUnitSystem, setDisplayUnitSystem] = useState(() => {
    return localStorage.getItem('replan_display_unit_system') || null;
  });

  const [shapeListVisible, setShapeListVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('replan_shapeListVisible');
      return stored && stored !== 'undefined' ? JSON.parse(stored) : false;
    } catch (e) {
      return false;
    }
  });

  const [displayKeysPanelVisible, setDisplayKeysPanelVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('replan_displayKeysPanelVisible');
      return stored && stored !== 'undefined' ? JSON.parse(stored) : true;
    } catch (e) {
      return true;
    }
  });

  const selectionColor = useMemo(() => {
    if (typeof window === 'undefined') return '#3B82F6';
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--pico-primary')
      .trim() || '#3B82F6';
  }, []);

  const [overlayEnabled, setOverlayEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('replan_overlay_enabled');
      return stored && stored !== 'undefined' ? JSON.parse(stored) : true;
    } catch (e) {
      return true;
    }
  });


  // --- Persistence Effects for UI State ---
  useEffect(() => {
    localStorage.setItem('replan_grouping_key', groupingKey || '');
  }, [groupingKey]);

  useEffect(() => {
    localStorage.setItem('replan_display_keys', JSON.stringify(displayKeys));
  }, [displayKeys]);

  useEffect(() => {
    localStorage.setItem('replan_overlay_enabled', JSON.stringify(overlayEnabled));
  }, [overlayEnabled]);

  useEffect(() => {
    if (displayUnitSystem) {
      localStorage.setItem('replan_display_unit_system', displayUnitSystem);
    } else {
      localStorage.removeItem('replan_display_unit_system');
    }
  }, [displayUnitSystem]);

  useEffect(() => {
    localStorage.setItem('replan_shapeListVisible', JSON.stringify(shapeListVisible));
  }, [shapeListVisible]);

  useEffect(() => {
    localStorage.setItem('replan_displayKeysPanelVisible', JSON.stringify(displayKeysPanelVisible));
  }, [displayKeysPanelVisible]);

  // --- Hooks ---
  const containerSize = useComponentSize(containerRef);

  // Hook to load webp images from the server natively
  const {
    konvaImage: webpKonvaImage,
    isReleased: webpImageReleased,
    clearImage: clearWebpImage,
    imageSize: webpImageSize,
    pages,
    currentImageIndex,
    currentPageData,
    numImages,
    currentPageName,
    isLoading: isWebpLoading,
    error,
    overlayImage,
    isOverlayLoading,
    loadOverlayImage,
    goToPage,
    goToPageByName,
    pageReferences,
    navigateToPageReference,
  } = useImageLoader(projectName, versionName, selectedFolder, { disableImageFetch: false, hasDocuments: projectHasDocuments });

  const getPageIndexInVersion = useCallback(
    (targetPage, targetVersion) => {
      if (!pages || pages.length === 0) return 0;
      
      const versionPages = [];
      pages.forEach((p) => {
        if (p.version === targetVersion) {
          versionPages.push(p);
        } else if (Array.isArray(p.alternativeVersions)) {
          const alt = p.alternativeVersions.find((a) => a.version === targetVersion);
          if (alt) {
            versionPages.push(alt);
          }
        }
      });

      const index = versionPages.findIndex(
        (p) =>
          p.documentId === targetPage.documentId &&
          String(p.actualPageNumber) === String(targetPage.actualPageNumber),
      );
      // BUG FIX (Fix 3): warn when falling back to index 0, which will
      // overwrite prior per-page measurement data for this version.
      if (index === -1) {
        console.warn(
          `[Replan] getPageIndexInVersion: page with actualPageNumber=${targetPage?.actualPageNumber} ` +
          `not found in version=${targetVersion}. pages.length=${pages.length}. ` +
          `If pages.length is non-zero, the version may be in the excludeVersions list. ` +
          `Falling back to index 0, which will overwrite prior per-page data.`,
        );
      }
      return index !== -1 ? index : 0;
    },
    [pages],
  );

  // Reset fallback mode ONLY when project or version changes (not every page turn)
  useEffect(() => {
    setIsFallbackMode(false);
  }, [projectName, versionName]);

  // Reset filters when project changes to prevent stale folder/version references
  useEffect(() => {
    setSelectedFolder('');
    setVersionName('');
  }, [projectName]);




  const getCurrentDraftContext = useCallback(() => {
    const organization = currentPageData?.organization;
    const currentPageVersion = currentPageData?.version;

    if (
      !firebase ||
      !authUser?.uid ||
      !projectName ||
      !organization ||
      !currentPageVersion ||
      typeof currentImageIndex !== 'number'
    ) {
      return null;
    }

    const pageIndexToUse = getPageIndexInVersion(currentPageData, currentPageVersion);

    return {
      organization,
      version: currentPageVersion,
      pageIndex: pageIndexToUse,
    };
  }, [
    authUser?.uid,
    currentImageIndex,
    currentPageData,
    firebase,
    projectName,
    getPageIndexInVersion,
  ]);

  // --- Deferred PDF Streaming: only start the heavy PDF pipeline when user
  // zooms past a threshold. Below this, the lightweight WebP is sharp enough.
  // Once triggered per page, the flag stays true (no point discarding a loaded PDF).
  const PDF_STREAM_ZOOM_THRESHOLD = 1;
  const [pdfStreamTriggered, setPdfStreamTriggered] = useState(false);

  // Reset the trigger when the page changes so each page starts with WebP
  useEffect(() => {
    setPdfStreamTriggered(false);
  }, [currentImageIndex, projectName]);

  // Note: The zoom trigger effect is placed after safeStageScale is defined (below).

  const pauseStream = (!webpKonvaImage && !error && !webpImageReleased)
    || (!pdfStreamTriggered && webpKonvaImage); // Keep paused until zoom threshold reached

  // Hook to stream raw PDFs natively (Progressive Enhancement)
  const {
    basePdfImage,
    pdfDimensions,
    highResImage,
    highResBounds,
    isPdfLoading,
    isRenderingHighRes,
    loadingStatus,
    pdfError,
    updateHighResViewport
  } = usePdfStreaming(
    currentPageData,
    containerSize,
    {
      debounceMs: 50, // Minimal debounce — interaction gate handles protection
      pauseStream,
      isInteracting: isStageInteracting,
      firebase,
      projectName,
    }
  );

  // Hook to stream overlay PDFs natively
  const {
    basePdfImage: overlayPdfBase,
    pdfDimensions: overlayPdfDimensions,
    highResImage: overlayPdfHighRes,
    highResBounds: overlayPdfHighResBounds,
    updateHighResViewport: updateOverlayHighResViewport,
  } = usePdfStreaming(
    overlayPdfData,
    containerSize,
    {
      debounceMs: 80, // Slightly staggered to avoid dual-render contention
      tintColor: overlayFilterEnabled ? overlayTintColor : null,
      pauseStream,
      isInteracting: isStageInteracting,
      firebase,
      projectName,
    }
  );

  // Free WebP memory when PDF is ready
  useEffect(() => {
    if (basePdfImage && webpKonvaImage) {
      clearWebpImage();
    }
  }, [basePdfImage, webpKonvaImage, clearWebpImage]);

  // Trigger fallback if PDF fails
  useEffect(() => {
    if (pdfError) {
      console.warn("PDF Stream failed, enabling WebP fallback:", pdfError);
      setIsFallbackMode(true);
    }
  }, [pdfError]);

  // Unified Rendering and Sizing Constants - Progressive Enhancement
  // The WebP serves as an instant placeholder while the PDF is rendering.
  const konvaImage = basePdfImage ? basePdfImage : webpKonvaImage;

  const dynamicDPI = useMemo(() => {
    return currentPageData?.dpi || 72;
  }, [currentPageData]);

  // CRITICAL: We ALWAYS use the WebP's intended pixel Resolution as our 'Master Coordinate Grid'.
  // We infer this grid by combining the PDF's natural units with the document's Metadata DPI.
  const imageSize = useMemo(() => {
    // 1. Actual Image: If we've already loaded the WebP (fallback mode), it is the ground truth.
    if (webpImageSize.width > 0) return webpImageSize;

    // 2. Intelligent Inference: Most Construction WebPs are scaled from PDF points (72 DPI)
    // to a higher metadata DPI (e.g., 150 or 300). We reconstruct that grid here.
    if (pdfDimensions && pdfDimensions.width > 0 && dynamicDPI > 0) {
      const scale = dynamicDPI / 72;
      return {
        width: Math.round(pdfDimensions.width * scale),
        height: Math.round(pdfDimensions.height * scale)
      };
    }

    // 3. Metadata Fallback: Use dimensions from doc info if available.
    if (currentPageData?.width && currentPageData?.height) {
      return { width: currentPageData.width, height: currentPageData.height };
    }

    // 4. Emergency Fallback: Use PDF units if nothing else is ready.
    if (pdfDimensions && pdfDimensions.width > 0) return pdfDimensions;
    return webpImageSize; // Fallback to {0, 0}
  }, [webpImageSize, currentPageData, pdfDimensions, dynamicDPI]);

  const isLoading = !konvaImage && (isWebpLoading || isPdfLoading);

  // Content the stage fits/initializes around: the plan image once a document
  // exists, otherwise the blank A0 sheet so pan/zoom behave like a CAD view.
  const stageContentSize = useMemo(
    () =>
      projectHasDocuments
        ? imageSize
        : { width: EMPTY_SHEET_WIDTH, height: EMPTY_SHEET_HEIGHT },
    [projectHasDocuments, imageSize],
  );

  const [emptySheetColors, setEmptySheetColors] = useState(readEmptySheetColors);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setEmptySheetColors(readEmptySheetColors());
    // data-theme is applied by Navigation's effect, which can run after ours,
    // so re-read once post-mount and again on every theme change.
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Empty-sheet text: measure the real rendered widths so the clickable group
  // hugs its content (fit-content), and a small tooltip on the action line.
  const emptyTitleText = t('replan.no_plans_title', {
    defaultValue: 'No plans uploaded yet',
  });
  const emptyActionText = t('replan.upload_plans', {
    defaultValue: 'Upload a plan',
  });
  const emptyVControlText = t('replan.open_vcontrol', {
    defaultValue: 'Open VControl',
  });
  const emptyUploadHintText = t('replan.upload_plans_hint', {
    defaultValue: 'Click to upload a plan',
  });
  const emptyVControlHintText = t('replan.open_vcontrol_hint', {
    defaultValue: 'Open VControl to manage documents',
  });
  const emptyTitleRef = useRef(null);
  const emptyActionRef = useRef(null);
  const emptyVControlRef = useRef(null);
  const [emptyTextWidths, setEmptyTextWidths] = useState({
    title: 0,
    action: 0,
    vcontrol: 0,
  });
  const [emptyTooltip, setEmptyTooltip] = useState(null); // { x, y, text } in container px
  // Hover state for the empty-sheet links, fed into the global cursor
  // resolution so the pointer isn't stomped by the 'grab' pan cursor.
  const [emptyLinkHovered, setEmptyLinkHovered] = useState(false);

  const handleEmptyTooltip = (e, text) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setEmptyTooltip({
      x: e.evt.clientX - rect.left + 14,
      y: e.evt.clientY - rect.top + 14,
      text,
    });
  };


  // 2. Call useStageInteraction, now with a properly evaluated imageSize.
  const {
    stageScale: rawStageScale,
    stagePos,
    handleWheel,
    resetView,
    handleStageDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isMultiTouchActive, // Get new touch handlers
  } = useStageInteraction(
    stageRef,
    stageContentSize,
    containerSize,
    // Treat "empty sheet" and "document loaded" as different view targets so
    // the stage re-fits when a plan is published (and vice versa).
    projectHasDocuments ? projectName : `${projectName}::empty`,
  ); // Pass projectName to preserve zoom

  // 3. Calculate safeStageScale based on rawStageScale from useStageInteraction.
  const safeStageScale = useMemo(() => {
    if (
      typeof rawStageScale === 'number' &&
      isFinite(rawStageScale) &&
      rawStageScale > 0
    ) {
      return rawStageScale;
    }
    return 1.0; // Default to 1.0 if unsafe
  }, [rawStageScale]);

  useEffect(() => {
    // Re-measure when zoom (font size) or content changes; widths are in
    // unscaled local units, independent of the stage transform.
    const raf = requestAnimationFrame(() => {
      const titleW = emptyTitleRef.current?.getTextWidth?.() || 0;
      const actionW = emptyActionRef.current?.getTextWidth?.() || 0;
      const vcontrolW = emptyVControlRef.current?.getTextWidth?.() || 0;
      setEmptyTextWidths((prev) =>
        prev.title === titleW &&
        prev.action === actionW &&
        prev.vcontrol === vcontrolW
          ? prev
          : { title: titleW, action: actionW, vcontrol: vcontrolW },
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [
    safeStageScale,
    emptyTitleText,
    emptyActionText,
    emptyVControlText,
    projectHasDocuments,
  ]);

  // Same-row layout for the two empty-sheet links (a centered pair).
  const emptyLinkGap = (19 * 6) * 0.5;
  const emptyActionW = emptyTextWidths.action || EMPTY_SHEET_WIDTH * 0.35;
  const emptyVcW = emptyTextWidths.vcontrol || EMPTY_SHEET_WIDTH * 0.35;
  const emptyRowW = emptyActionW + emptyLinkGap + emptyVcW;
  const emptyRowX = (EMPTY_SHEET_WIDTH - emptyRowW) / 2;
  const emptyRowY = EMPTY_SHEET_HEIGHT * 0.4;

  // Trigger PDF streaming when zoom exceeds threshold (placed here because
  // safeStageScale must be defined before it can be referenced)
  useEffect(() => {
    if (!pdfStreamTriggered && safeStageScale >= PDF_STREAM_ZOOM_THRESHOLD) {
      setPdfStreamTriggered(true);
    }
  }, [safeStageScale, pdfStreamTriggered]);

  // 4. Trigger High Res Update whenever stage transforms, but ONLY when user stops moving
  useEffect(() => {
    if (stagePos && !isStageInteracting) {
      // Round coordinates to avoid tiny floating point drifts triggering background renders
      const roundedX = Math.round(stagePos.x);
      const roundedY = Math.round(stagePos.y);
      const roundedScale = Math.round(safeStageScale * 100) / 100;

      // Calculate the scaling factor between the Master WebP Grid and the PDF Points
      const pdfToWebpScale = (pdfDimensions && pdfDimensions.width > 0 && imageSize.width > 0)
        ? pdfDimensions.width / imageSize.width
        : 1;

      if (basePdfImage && updateHighResViewport) {
        updateHighResViewport(safeStageScale, stagePos, !!overlayImage || !!overlayPdfData, pdfToWebpScale);
      }
      // Skip overlay high-res patches in comparison mode — the overlay is for
      // diff visualization, not zoomed reading, and halving renders reduces lag.
      // if (overlayPdfBase && updateOverlayHighResViewport) {
      //   updateOverlayHighResViewport(safeStageScale, stagePos, true, pdfToWebpScale);
      // }
    }
  }, [
    Math.round(stagePos?.x || 0),
    Math.round(stagePos?.y || 0),
    Math.round(safeStageScale * 100) / 100,
    basePdfImage,
    !!overlayImage,
    !!overlayPdfData,
    overlayPdfBase,
    pdfDimensions?.width,
    imageSize.width,
    isStageInteracting,
    updateHighResViewport,
    updateOverlayHighResViewport
  ]);

  const handleCreateMeasurementSummaryRef = useRef();
  const triggerCreateMeasurementSummary = useCallback(() => {
    if (handleCreateMeasurementSummaryRef.current) {
      handleCreateMeasurementSummaryRef.current();
    }
  }, []);

  // --- All Scales for Per-Shape Resolution ---
  const allScales = useMemo(() => {
    return [...METRIC_SCALES, ...IMPERIAL_SCALES, ...(customScales || [])];
  }, [customScales]);

  const {
    selectedTool,
    selectTool,
    lastActiveTool,
    isDrawing,
    shapes,
    setShapes: rawSetShapes,
    currentShape,
    setCurrentShape,
    setIsDrawing,
    selectedShapeIds,
    setSelectedShapeIds,
    strokeColor,
    handleStrokeColorChange,
    snapIndicator,
    visibleSnapPoints,
    strokeWidth,
    handleStrokeWidthChange,
    setStrokeWidth,
    selectionRect,
    setSelectionRect,
    handlePointerDown,
    handlePointerMove: drawingHandleMouseMove,
    handlePointerUp,
    handlePointerMove,
    handleDoubleClick,
    handleShapeClick,
    handleShapeDragEnd,
    handleVertexClick,
    handleTransformEnd,
    resetDrawingState,
    measurementSystem,
    setMeasurementSystem,
    selectedScaleKey,
    setSelectedScaleKey,
    currentScaleProps,
    areaOperationMode,
    selectedPolygonsForArea,
    executeBooleanOperation,
    undo,
    redo,
    canUndo,
    canRedo,
    isOrthogonalModeActive,
    setIsOrthogonalModeActive,
    isRectangleMode,
    setIsRectangleMode,
    isDraggingMode,
    setIsDraggingMode,
    isSnappingEnabled,
    setIsSnappingEnabled,
    isGridVisible,
    setIsGridVisible,
    vertexEditInfo,
    setVertexEditInfo,
    insertVertexMode,
    setInsertVertexMode,
    hoveredSegmentInfo,
    selectedPointMarker,
    setSelectedPointMarker,
    blockScaleForShapes,
    unblockScaleForShapes,
  } = useDrawing(
    stageRef,
    transformerRef,
    safeStageScale,
    projectName,
    currentPageData,
    currentImageIndex,
    customScales,
    imageSize, // Pass imageSize for grid snapping
    triggerCreateMeasurementSummary,
    {
      displayKeys,
      setDisplayKeys,
      displayUnitSystem,
      setDisplayUnitSystem,
      displayKeysPanelVisible,
      setDisplayKeysPanelVisible,
    },
    allScales // Pass allScales for per-shape scale resolution
  );

  // Wrap setShapes to compute measurements inline (eliminates post-hoc cascade effect)
  const setShapes = useCallback((updater) => {
    rawSetShapes(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!Array.isArray(next)) return next;
      return next.map(shape => {
        if (!shape?.type) return shape;
        if (shape.type === 'POLYGON') shape = { ...shape, type: TOOLS.AREA };
        const cacheKey = `${shape.scaleKey || currentScaleProps.key}_${dynamicDPI}_${displayUnitSystem || 'auto'}`;
        const hasValidMeasurements =
          shape._measurementCacheKey === cacheKey &&
          (shape.type !== TOOLS.MEASURE || shape.displayMeasurement) &&
          (shape.type !== TOOLS.POLYLINE || shape.displayTotalMeasurement) &&
          (shape.type !== TOOLS.AREA || shape.displayAreaMeasurement);
        if (hasValidMeasurements) return shape;
        const effectiveScaleProps = resolveShapeScaleProps(shape, currentScaleProps, allScales);
        const enhanced = enhanceShapeWithMeasurements(shape, effectiveScaleProps, dynamicDPI, imageSize, displayUnitSystem);
        enhanced._measurementCacheKey = cacheKey;
        return enhanced;
      });
    });
  }, [rawSetShapes, currentScaleProps, allScales, dynamicDPI, imageSize, displayUnitSystem]);

  // Refs to stabilize loadPageData dependencies and prevent the reversion loop
  const setShapesRef = React.useRef(setShapes);
  const setMeasurementSystemRef = React.useRef(setMeasurementSystem);
  const setSelectedScaleKeyRef = React.useRef(setSelectedScaleKey);

  // Sync refs on every render
  React.useEffect(() => {
    setShapesRef.current = setShapes;
    setMeasurementSystemRef.current = setMeasurementSystem;
    setSelectedScaleKeyRef.current = setSelectedScaleKey;
  });

  // Re-enhance existing shapes when image size, scaling, or display settings change
  useEffect(() => {
    if (!hasLoadedCurrentPageRef.current) return;
    setShapes(prev => prev);
  }, [setShapes]);



  // Debounced scale for shape re-renders: shapes use a delayed copy during zoom
  const [debouncedScale, setDebouncedScale] = useState(safeStageScale);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedScale(safeStageScale), 80);
    return () => clearTimeout(timer);
  }, [safeStageScale]);

  // --- Annotation tool group state ---
  const [selectedAnnotationTool, setSelectedAnnotationTool] = useState(TOOLS.TEXT);

  // --- Per-Shape Scale Blocking ---
  const allSelectedBlockedAtCurrentScale = useMemo(() => {
    if (selectedShapeIds.length === 0) return false;
    return selectedShapeIds.every(id => {
      const shape = shapes.find(s => s.id === id);
      return shape && shape.scaleKey === selectedScaleKey;
    });
  }, [selectedShapeIds, shapes, selectedScaleKey]);

  const handleBlockScale = useCallback(() => {
    if (!selectedShapeIds || selectedShapeIds.length === 0) {
      alert('Please select shapes to block/unblock scale.');
      return;
    }
    if (allSelectedBlockedAtCurrentScale) {
      unblockScaleForShapes(selectedShapeIds);
    } else {
      blockScaleForShapes(selectedShapeIds);
    }
  }, [allSelectedBlockedAtCurrentScale, selectedShapeIds, blockScaleForShapes, unblockScaleForShapes]);

  const getLockedScaleLabel = useCallback((shape) => {
    if (!shape || !shape.scaleKey) return null;
    const found = allScales.find(s => s.key === shape.scaleKey);
    return found ? found.label : null;
  }, [allScales]);

  // --- Interactive Transform Hook ---
  const {
    transformMode,
    isTransformActive,
    transformPreview,
    transformSnapIndicator,
    startTransform,
    cancelTransform,
    handleTransformPointerDown,
    handleTransformPointerMove,
  } = useInteractiveTransform(
    shapes,
    selectedShapeIds,
    setShapes,
    stageRef,
    { stageScale: safeStageScale, isSnappingEnabled, isOrthogonalModeActive, imageSize },
  );

  // --- Dynamic DPI Calculation ---

  const getCurrentDraftState = useCallback(() => {
    const dpiForSave = dynamicDPI || currentPageData?.dpi || 72;
    const payload = buildPageDraftPayload({
      shapes,
      currentShape,
      isDrawing,
      measurementSystem,
      selectedScaleKey,
      dpiForSave,
      displayUnitSystem,
    });

    return {
      payload,
      snapshot: createComparablePageSnapshot(payload),
    };
  }, [
    currentPageData,
    dynamicDPI,
    isDrawing,
    measurementSystem,
    currentShape,
    selectedScaleKey,
    shapes,
  ]);

  const buildLatestDraftState = useCallback(() => {
    const organization = currentPageData?.organization;
    const currentPageVersion = currentPageData?.version;
    const pageIndexToUse = getPageIndexInVersion(currentPageData, currentPageVersion);
    const currentPageKey = getDraftStateKeyFromContext({
      organization,
      projectName,
      versionName: currentPageVersion,
      pageIndex: pageIndexToUse,
    });

    if (
      !firebase ||
      !authUser?.uid ||
      !projectName ||
      !organization ||
      !currentPageVersion ||
      typeof currentImageIndex !== 'number' ||
      !hasLoadedCurrentPageRef.current ||
      !currentPageKey ||
      loadedDraftPageKeyRef.current !== currentPageKey
    ) {
      return null;
    }

    const { payload, snapshot } = getCurrentDraftState();

    return {
      authUid: authUser.uid,
      organization,
      projectName,
      version: currentPageVersion,
      pageIndex: pageIndexToUse,
      pageKey: currentPageKey,
      payload,
      snapshot,
      publishedSnapshot: publishedPageSnapshotRef.current,
    };
  }, [
    authUser?.uid,
    currentImageIndex,
    currentPageData,
    firebase,
    getCurrentDraftState,
    projectName,
    getPageIndexInVersion,
  ]);

  const saveDraftState = useCallback(async (draftState) => {
    if (!firebase || !draftState) {
      return false;
    }

    const draftPath = buildReplanDraftPreferencePath(
      draftState.organization,
      draftState.projectName,
      draftState.version,
      draftState.pageIndex,
    );
    const draftIndexPath = buildReplanDraftIndexPreferencePath(
      draftState.organization,
      draftState.projectName,
      draftState.version,
      draftState.pageIndex,
    );

    try {
      if (
        arePageSnapshotsEquivalent(
          draftState.snapshot,
          draftState.publishedSnapshot,
        )
      ) {
        await firebase.doRemoveUserPreference(
          draftState.authUid,
          draftPath,
        );

        const draftIndexCleanup = await Promise.allSettled([
          firebase.doRemoveUserPreference(
            draftState.authUid,
            draftIndexPath,
          ),
        ]);

        draftIndexCleanup.forEach((result) => {
          if (result.status === 'rejected') {
            console.warn(
              'Replan draft payload was cleared, but draft index cleanup failed:',
              result.reason,
            );
          }
        });

        draftStateByPageKeyRef.current.delete(draftState.pageKey);

        if (loadedDraftPageKeyRef.current === draftState.pageKey) {
          latestDraftStateRef.current = null;
          privateDraftSnapshotRef.current = draftState.publishedSnapshot;
          setShowDiscardDraft(false);
        }

        return true;
      } else {
        await firebase.doSetUserPreference(
          draftState.authUid,
          draftPath,
          draftState.payload,
        );
        await firebase.doSetUserPreference(
          draftState.authUid,
          draftIndexPath,
          { updatedAt: draftState.payload.updatedAt || Date.now() },
        );
      }

      return true;
    } catch (error) {
      console.error('Error saving Replan draft:', error);
      return false;
    }
  }, [firebase]);

  const saveDraftIfNeeded = useCallback(async () => {
    return saveDraftState(latestDraftStateRef.current);
  }, [saveDraftState]);

  const clearScheduledDraftSave = useCallback(() => {
    if (draftAutosaveTimeoutRef.current) {
      clearTimeout(draftAutosaveTimeoutRef.current);
      draftAutosaveTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    saveDraftIfNeededRef.current = saveDraftIfNeeded;
  }, [saveDraftIfNeeded]);

  useEffect(() => {
    const latestDraftState = buildLatestDraftState();

    latestDraftStateRef.current = latestDraftState;
    clearScheduledDraftSave();

    if (latestDraftState?.pageKey) {
      draftStateByPageKeyRef.current.set(
        latestDraftState.pageKey,
        latestDraftState,
      );

      draftAutosaveTimeoutRef.current = setTimeout(() => {
        void saveDraftState(latestDraftState);
      }, DRAFT_AUTOSAVE_DELAY_MS);
    }

    return () => {
      clearScheduledDraftSave();
    };
  }, [buildLatestDraftState, clearScheduledDraftSave, saveDraftState]);

  const setProjectNameSynced = useCallback(
    (nextProjectName) => {
      const next = nextProjectName || '';
      setProjectName(next);
      setGlobalSelectedProject(next);
      // Reset version and folder for the new project context
      setVersionName('');
      setSelectedFolder('');
    },
    [setGlobalSelectedProject],
  );

  // Hook to sync URL with viewer state (project, version, folder, page)
  useUrlSync({
    projectName,
    versionName,
    selectedFolder,
    currentPageName,
    pages,
    currentImageIndex,
    onProjectChange: setProjectNameSynced,
    onVersionChange: setVersionName,
    onFolderChange: setSelectedFolder,
    goToPage,
    goToPageByName, // Pass the name-based navigation function
    pageRestoreSettled: savedPageRestored,
  });

  // --- Deduplicate shapes to prevent duplicate keys ---
  const deduplicatedShapes = useMemo(() => {
    return deduplicateShapes(shapes);
  }, [shapes]);

  // Memoize selected shape IDs as a Set for O(1) lookup in render sort
  const selectedSet = useMemo(() => new Set(selectedShapeIds), [selectedShapeIds]);

  const allParamKeys = useMemo(() => {
    const keys = new Set();
    deduplicatedShapes.forEach((shape) => {
      if (shape.params) {
        Object.keys(shape.params).forEach((key) => keys.add(key));
      }
    });
    return Array.from(keys).sort();
  }, [deduplicatedShapes]);

  // --- Memory Cleanup Effect ---
  useEffect(() => {
    // Clear selections when page changes to prevent stale references
    setSelectedShapeIds([]);
  }, [currentImageIndex, setSelectedShapeIds]); // Clear selections when page changes

  // --- Load Custom Scales from Firebase ---
  useEffect(() => {
    if (authUser && firebase) {
      firebase.doGetUserPreference(authUser.uid, 'customScales')
        .then(scales => setCustomScales(scales || []))
        .catch(err => console.error('Error loading custom scales:', err));
    }
  }, [authUser, firebase]);

  // --- Effect to fetch pages with shapes for thumbnail badges ---
  useEffect(() => {
    if (!firebase || !projectName || !pages || pages.length === 0) {
      setPagesWithShapes(new Map());
      return;
    }

    // Extract unique versions from the pages array
    const uniqueVersions = [
      ...new Set(pages.map((p) => p.version).filter(Boolean)),
    ];

    if (uniqueVersions.length === 0) {
      setPagesWithShapes(new Map());
      return;
    }

    const fetchPagesWithShapes = async () => {
      try {
        // Get organization from first page (all pages in a project have same organization)
        const organization = pages[0]?.organization;
        if (!organization) {
          console.warn('No organization found in page data');
          setPagesWithShapes(new Map());
          return;
        }
        const allPagesMap = await firebase.doGetAllPagesWithShapes(
          organization,
          projectName,
          uniqueVersions,
        );
        // console.log('Pages with shapes:', allPagesMap);
        setPagesWithShapes(allPagesMap);
      } catch (error) {
        console.error('Failed to fetch pages with shapes:', error);
        setPagesWithShapes(new Map());
      }
    };

    fetchPagesWithShapes();
  }, [firebase, projectName, pages]);

  // --- Effect to Load Shapes from Selected Foreign Versions ---
  // Inject into `shapes` so they appear in the main list and are interactive on canvas.
  // Save filters out shapes with `sourceVersion`.
  useEffect(() => {
    const contextKey = `${currentImageIndex}:${projectName}:${[...selectedVersionShapes].sort().join(',')}`;

    if (
      !firebase ||
      !projectName ||
      typeof currentImageIndex !== 'number' ||
      !currentPageData?.organization
    ) {
      setShapes((prev) => prev.filter((s) => !s.sourceVersion));
      return;
    }

    // Clear previous foreign shapes immediately
    setShapes((prev) => prev.filter((s) => !s.sourceVersion));

    if (selectedVersionShapes.length === 0) return;

    const organization = currentPageData.organization;
    let cancelled = false;

    const loadForeignShapes = async () => {
      const loaded = [];

      for (const version of selectedVersionShapes) {
        if (cancelled) return;
        try {
          const alt = currentPageData?.alternativeVersions?.find(
            (a) => a.version === version,
          );
          const pageIndexToUse = alt
            ? getPageIndexInVersion(alt, version)
            : getPageIndexInVersion(currentPageData, version);

          const doc = await firebase.doGetPageData(
            organization,
            projectName,
            version,
            pageIndexToUse,
          );
          const data = doc.exists ? doc.data() : null;
          if (!data || cancelled) continue;

          const sourceShapes = getOrderedShapesFromPageData(data);
          const shapeDPI = dynamicDPI || data.dpi || 72;
          const transformedShapes = sourceShapes.map((shape) => {
            const sDPI =
              shape.storedDPI || shape.originalDPI || shapeDPI;
            const factor = dynamicDPI / sDPI;
            if (factor === 1) return shape;
            return {
              ...shape,
              x: shape.x !== undefined ? shape.x * factor : shape.x,
              y: shape.y !== undefined ? shape.y * factor : shape.y,
              width:
                shape.width !== undefined
                  ? shape.width * factor
                  : shape.width,
              height:
                shape.height !== undefined
                  ? shape.height * factor
                  : shape.height,
              radius:
                shape.radius !== undefined
                  ? shape.radius * factor
                  : shape.radius,
              points: shape.points?.map((v) => v * factor),
              strokeWidth:
                shape.strokeWidth !== undefined
                  ? shape.strokeWidth * factor
                  : 4,
              holes: shape.holes?.map((hole) =>
                hole.map((v) => v * factor),
              ),
            };
          });

          const deduplicated = deduplicateShapes(transformedShapes);
          const loadedScaleKey =
            data.selectedScaleKey ||
            getDefaultScaleKeyForMeasurementSystem(
              data.measurementSystem || 'metric',
            );
          const loadedScaleProps =
            allScales.find((s) => s.key === loadedScaleKey) ||
            (data.measurementSystem === 'imperial'
              ? IMPERIAL_SCALES[0]
              : METRIC_SCALES[0]);

          const enhanced = deduplicated.map((shape) => {
            const effectiveScaleProps = resolveShapeScaleProps(
              shape,
              loadedScaleProps,
              allScales,
            );
            const enhancedShape = enhanceShapeWithMeasurements(
              shape,
              effectiveScaleProps,
              dynamicDPI,
              imageSize,
              displayUnitSystem,
            );
            return { ...enhancedShape, sourceVersion: version };
          });

          loaded.push(...enhanced);
        } catch (err) {
          console.warn(
            `Failed to load shapes from version ${version}:`,
            err,
          );
        }
      }

      if (!cancelled) {
        setShapes((prev) => {
          const current = prev.filter((s) => !s.sourceVersion);
          const existingIds = new Set(current.map((s) => s.id));
          const toAdd = loaded.filter((s) => !existingIds.has(s.id));
          return [...current, ...toAdd];
        });
      }
    };

    loadForeignShapes();

    return () => {
      cancelled = true;
    };
  }, [
    selectedVersionShapes,
    currentImageIndex,
    projectName,
    currentPageData?.organization,
    currentPageData?.alternativeVersions,
    firebase,
    dynamicDPI,
    imageSize,
    displayUnitSystem,
    allScales,
    getPageIndexInVersion,
  ]);

  // --- Event Handlers ---

  // When a transform is active, shape/vertex clicks should feed the transform
  // handler instead of the normal selection logic. Shape clicks set cancelBubble
  // so the Stage onMouseDown never fires - intercept here.
  // Ref to hold the latest transform handlers to bypass ShapeRenderer's React.memo stale closures
  const transformHandlersRef = useRef({
    isTransformActive,
    handleTransformPointerDown,
  });

  useEffect(() => {
    transformHandlersRef.current = {
      isTransformActive,
      handleTransformPointerDown,
    };
  }, [isTransformActive, handleTransformPointerDown]);

  const handleShapeClickWithPanel = useCallback(
    (e, shape) => {
      if (transformHandlersRef.current.isTransformActive) {
        e.cancelBubble = true;
        // Do NOT call handleTransformPointerDown here, because it already fired via Stage onMouseDown!
        return;
      }
      handleShapeClick(e, shape);
    },
    [handleShapeClick],
  );

  const handleVertexClickWithTransform = useCallback(
    (e, shapeId, vertexIndex, isHole, holeIndex) => {
      if (transformHandlersRef.current.isTransformActive) {
        e.cancelBubble = true;
        transformHandlersRef.current.handleTransformPointerDown(e);
        return;
      }
      handleVertexClick(e, shapeId, vertexIndex, isHole, holeIndex);
    },
    [handleVertexClick],
  );

  // Throttled mouse position updater to reduce RAM usage
  const updateMousePosition = useCallback(
    throttle((x, y) => {
      setMousePos({ x, y });
    }, 16), // ~60fps
    [],
  );

  // Combined Stage Mouse Move Handler
  const handleStageMouseMove = useCallback(
    (e) => {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getRelativePointerPosition();
      if (pos) {
        updateMousePosition(pos.x, pos.y); // Use throttled update
      }

      // If a transform mode is active, route to transform handler instead of drawing
      if (isTransformActive) {
        handleTransformPointerMove(e);
        return;
      }

      // Otherwise call the drawing hook's handler
      drawingHandleMouseMove(e);
    },
    [stageRef, drawingHandleMouseMove, updateMousePosition, isTransformActive, handleTransformPointerMove],
  );

  // --- Shape Arrangement Handler ---
  const handleArrange = useCallback(
    (direction) => {
      if (selectedShapeIds.length === 0) return;

      setShapes((currentShapes) => {
        let newShapes = [...currentShapes];
        const selectedShapes = newShapes.filter((s) =>
          selectedShapeIds.includes(s.id),
        );
        const otherShapes = newShapes.filter(
          (s) => !selectedShapeIds.includes(s.id),
        );

        switch (direction) {
          case 'front':
            return [...otherShapes, ...selectedShapes];
          case 'back':
            return [...selectedShapes, ...otherShapes];
          case 'forward':
            selectedShapeIds.forEach((id) => {
              const index = newShapes.findIndex((s) => s.id === id);
              if (index < newShapes.length - 1) {
                const item = newShapes.splice(index, 1)[0];
                newShapes.splice(index + 1, 0, item);
              }
            });
            return newShapes;
          case 'backward':
            [...selectedShapeIds].reverse().forEach((id) => {
              const index = newShapes.findIndex((s) => s.id === id);
              if (index > 0) {
                const item = newShapes.splice(index, 1)[0];
                newShapes.splice(index - 1, 0, item);
              }
            });
            return newShapes;
          default:
            return currentShapes;
        }
      });
    },
    [selectedShapeIds, setShapes],
  );

  // --- Unified Gesture Handlers ---
  const handleUnifiedPointerDown = useCallback(
    (e) => {
      const touches = e.evt.touches;
      const stage = stageRef.current;
      if (!stage) return;

      if (touches && touches.length >= 2) {
        // Multi-touch: stop any dragging and disable it.
        stage.stopDrag();
        stage.draggable(false);
        handleTouchStart(e);
      } else {
        // Single touch/mouse: route to transform or drawing handler
        if (isTransformActive && handleTransformPointerDown(e)) return;
        handlePointerDown(e);
      }
    },
    [handleTouchStart, handlePointerDown, isTransformActive, handleTransformPointerDown],
  );

  const handleUnifiedPointerMove = useCallback(
    (e) => {
      const touches = e.evt.touches;

      if (touches && touches.length >= 2) {
        // Multi-touch: route to zoom handler (this will preventDefault)
        handleTouchMove(e);
      } else {
        // Single touch: prevent page scroll/browser gestures while actively drawing
        if (isDrawing && e.evt.cancelable) {
          e.evt.preventDefault();
        }
        handleStageMouseMove(e);
      }
    },
    [handleTouchMove, handleStageMouseMove, isDrawing],
  );

  const handleUnifiedPointerUp = useCallback(
    (e) => {
      const wasMultiTouch = isMultiTouchActive(); // Use the helper

      // Always cleanup touch state first
      if (e.evt.touches !== undefined) {
        handleTouchEnd(e);
      }

      // After a multi-touch ends, re-enable dragging if the tool allows it.
      const stage = stageRef.current;
      if (stage && wasMultiTouch) {
        const canDrag =
          (selectedTool === TOOLS.SELECT && !isDrawing) ||
          selectedTool === TOOLS.NAVIGATION;
        stage.draggable(canDrag);
      }

      // Only call drawing end if it wasn't a multi-touch gesture
      if (!wasMultiTouch) {
        handlePointerUp(e);
      }
      // setIsTouchPanning(false); // REMOVED: No longer needed
    },
    [
      handleTouchEnd,
      handlePointerUp,
      isMultiTouchActive,
      selectedTool,
      isDrawing,
    ],
  );

  // Stage Mouse Leave Handler
  const handleStageMouseLeave = useCallback(() => {
    setMousePos({ x: null, y: null }); // Clear display state
  }, []);

  // Stage drag start handler
  const handleStageDragStart = useCallback(
    (e) => {
      // Only act if the stage itself is being dragged
      if (e.target === stageRef.current) {
        startInteraction();
      }
    },
    [startInteraction],
  );

  // Stage drag end handler
  const handleStageDragEndCombined = useCallback(
    (e) => {
      handleStageDragEnd(e); // Update position ZZvia hook
      if (e.target === stageRef.current) {
        stopInteraction();
      }
    },
    [handleStageDragEnd, stopInteraction],
  );

  const handleTransform = useCallback(
    (operation) => {
      if (selectedShapeIds.length === 0) return;

      // Delete is immediate, not interactive
      if (operation === 'delete') {
        setShapes((prevShapes) =>
          prevShapes.filter((s) => !selectedShapeIds.includes(s.id)),
        );
        setSelectedShapeIds([]); // clear selection so the panel hides and state stays consistent
        return;
      }

      // All other operations are interactive (click-based)
      startTransform(operation);
    },
    [selectedShapeIds, setShapes, setSelectedShapeIds, startTransform],
  );

  // Handler for shape selection from the list
  const handleShapeListItemSelect = useCallback(
    (shapeIds, shouldSelect) => {
      selectTool(TOOLS.SELECT); // Switch to select tool

      setSelectedShapeIds((prev) => {
        const idsToProcess = Array.isArray(shapeIds)
          ? shapeIds
          : [shapeIds];

        if (typeof shouldSelect === 'boolean') {
          // Group selection logic (select/deselect all)
          if (shouldSelect) {
            // Add all shape IDs that are not already selected
            const newIds = idsToProcess.filter(
              (id) => !prev.includes(id),
            );
            return [...prev, ...newIds];
          } else {
            // Remove all shape IDs from selection
            return prev.filter((id) => !idsToProcess.includes(id));
          }
        } else {
          // Single item toggle logic
          const id = idsToProcess[0];
          const isSelected = prev.includes(id);
          if (isSelected) {
            return prev.filter((prevId) => prevId !== id);
          } else {
            return [...prev, id];
          }
        }
      });
    },
    [selectTool, setSelectedShapeIds],
  );

  const handleUpdateShape = useCallback((shapeId, updates) => {
    setShapes((currentShapes) =>
      currentShapes.map((shape) =>
        shape.id === shapeId ? { ...shape, ...updates } : shape,
      ),
    );
  }, [setShapes]);

  const handleCopyForeignShape = useCallback((shape) => {
    const newShape = {
      ...shape,
      id: generateId(),
      sourceVersion: undefined,
      x: shape.x + 20,
      y: shape.y + 20,
    };
    setShapes((prev) => [...prev, newShape]);
    setSelectedShapeIds((prev) => [...prev, newShape.id]);
  }, []);

  // Handler for page selection from the dropdown
  const handlePageChange = useCallback(
    (pageNumber) => {
      goToPage(pageNumber - 1);
    },
    [goToPage],
  );

  /**
   * Handle search results from PdfControls
   * Updates local state to trigger highlight rendering and auto-centering
   * @param {Array} matches - Array of match objects with position data
   * @param {number} currentIndex - Index of currently selected match
   */
  const handleSearchResults = useCallback((matches, currentIndex) => {
    setSearchMatches(matches);
    setCurrentSearchIndex(currentIndex);
  }, []);

  // This handler now just updates the version name and calls the hook function for overlays
  const handleVersionChange = useCallback(
    (newVersionName, options) => {
      setVersionName(newVersionName);

      if (options?.navigateTo) {
        const { documentId, actualPageNumber } = options.navigateTo;
        const mode = options.mode || 'overlay';

        if (mode === 'page') {
          setPendingPageTarget({ documentId, actualPageNumber });
          loadOverlayImage(null, null);
          setOverlayPdfData(null);
          return;
        }

        // If the selected alternative is the same as the currently displayed page, clear the overlay.
        if (
          currentPageData &&
          currentPageData.documentId === documentId &&
          currentPageData.actualPageNumber === actualPageNumber
        ) {
          loadOverlayImage(null, null);
          setOverlayPdfData(null);
        } else {
          loadOverlayImage(documentId, actualPageNumber);
          setOverlayPdfData({ documentId, actualPageNumber });
        }
      } else {
        loadOverlayImage(null, null); // Clear overlay if no specific navigation
        setOverlayPdfData(null);
      }
    },
    [currentPageData, loadOverlayImage],
  );

  useEffect(() => {
    const handlePageHide = () => {
      clearScheduledDraftSave();
      void saveDraftIfNeededRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearScheduledDraftSave();
        void saveDraftIfNeededRef.current();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange,
    );

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
    };
  }, [clearScheduledDraftSave]);

  useEffect(() => {
    const pageIndexToUse = getPageIndexInVersion(currentPageData, currentPageData?.version);
    const pageKey = buildReplanDraftStateKey(
      currentPageData?.organization,
      projectName,
      currentPageData?.version,
      pageIndexToUse,
    );

    return () => {
      clearScheduledDraftSave();
      void saveDraftState(
        draftStateByPageKeyRef.current.get(pageKey) || null,
      );
    };
  }, [
    clearScheduledDraftSave,
    currentImageIndex,
    currentPageData,
    projectName,
    saveDraftState,
    getPageIndexInVersion,
  ]);

  useEffect(() => {
    if (!pendingPageTarget || pages.length === 0) {
      return;
    }

    const targetIndex = pages.findIndex(
      (page) =>
        page.documentId === pendingPageTarget.documentId &&
        page.actualPageNumber === pendingPageTarget.actualPageNumber,
    );

    if (targetIndex !== -1) {
      goToPage(targetIndex);
      // This navigation bypasses PdfControls' handlePageChange, so persist the
      // selected page here (stable identity) or the next restore goes stale.
      const page = pages[targetIndex];
      const pageIdentity = page?.pageLabel || page?.name;
      if (authUser?.uid && firebase && projectName && pageIdentity) {
        firebase
          .doSetUserPreference(
            authUser.uid,
            `replan/selectedPage/${projectName}`,
            pageIdentity,
          )
          .catch((err) =>
            console.error(
              'Failed to save selected Replan page:',
              err,
            ),
          );
      }
    }

    setPendingPageTarget(null);
  }, [pendingPageTarget, pages, goToPage, authUser?.uid, firebase, projectName]);

  useEffect(() => {
    if (!overlayImageRef.current) {
      return;
    }

    // Only cache when a filter is applied. Caching rasterizes the whole node
    // to an offscreen canvas (expensive); without a filter the plain KonvaImage
    // draws directly and needs no cache, so skipping it keeps pan/zoom smooth.
    if (overlayFilterEnabled) {
      overlayImageRef.current.cache();
    } else {
      overlayImageRef.current.clearCache();
    }
    overlayImageRef.current.getLayer()?.batchDraw();
  }, [overlayImage, overlayTintColor, overlayFilterEnabled]);

  useEffect(() => {
    if (!overlayModeEnabled) {
      loadOverlayImage(null, null);
      setOverlayPdfData(null);
    }
  }, [overlayModeEnabled, loadOverlayImage]);

  const handleFolderChange = useCallback(
    (newFolder) => {
      setSelectedFolder(newFolder);
      loadOverlayImage(null, null); // Clear overlay when folder changes
      setOverlayPdfData(null);
    },
    [loadOverlayImage],
  );

  const overlayTintRgb = useMemo(() => {
    const normalized = (overlayTintColor || '').replace('#', '');
    const hex = normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return { r: 255, g: 120, b: 0 };
    }

    return { r, g, b };
  }, [overlayTintColor]);

  // --- Effects ---

  // Effect to clear search highlights when page changes
  useEffect(() => {
    setSearchMatches([]);
    setCurrentSearchIndex(0);
  }, [currentImageIndex]);

  // Effect to update cursor based on selected tool and hover state
  useEffect(() => {
    const container = containerRef.current;
    const stage = stageRef.current;
    if (!container) return;

    // Helpers
    const multiTouchActive =
      typeof isMultiTouchActive === 'function'
        ? isMultiTouchActive()
        : false;
    const stageIsDragging =
      stage &&
      typeof stage.isDragging === 'function' &&
      stage.isDragging();
    const canDragStage =
      (selectedTool === TOOLS.SELECT && !isDrawing) ||
      selectedTool === TOOLS.NAVIGATION;

    let nextCursor = 'default';

    // Priority-based cursor resolution
    if (emptyLinkHovered) {
      nextCursor = 'pointer';
    } else if (isHoveringPageRef) {
      nextCursor = 'pointer';
    } else if (multiTouchActive || stageIsDragging) {
      nextCursor = 'grabbing';
    } else if (insertVertexMode || vertexEditInfo) {
      nextCursor = 'crosshair';
    } else if (isDrawing || isTransformActive) {
      nextCursor = 'crosshair';
    } else if (hoveredShapeId && selectedTool === TOOLS.SELECT) {
      nextCursor = isDraggingMode ? 'move' : 'default';
    } else if (isDraggingMode && canDragStage) {
      nextCursor = 'grab';
    } else if (selectedTool === TOOLS.NAVIGATION || canDragStage) {
      nextCursor = 'grab';
    } else if ([
      TOOLS.PEN, TOOLS.RECTANGLE, TOOLS.CIRCLE, TOOLS.LINE,
      TOOLS.MEASURE, TOOLS.POINT, TOOLS.POLYLINE, TOOLS.POLYGON, TOOLS.TEXT
    ].includes(selectedTool)) {
      nextCursor = 'crosshair';
    }

    if (stage) {
      stage.container().style.cursor = nextCursor;
    }
    // Also set it on the parent container as a fallback
    container.style.cursor = nextCursor;
  }, [
    selectedTool,
    isDrawing,
    isTransformActive,
    isDraggingMode,
    hoveredShapeId,
    vertexEditInfo,
    insertVertexMode,
    isHoveringPageRef,
    emptyLinkHovered,
    // function from useStageInteraction; include so multi-touch changes update cursor
    isMultiTouchActive,
    isStageInteracting,
  ]);

  // --- Data Persistence Effects ---

  // Effect to LOAD page data from Firebase when context changes
  useEffect(() => {
    // Ensure we have a valid context before trying to load
    if (
      !firebase ||
      !projectName ||
      typeof currentImageIndex !== 'number'
    ) {
      loadRequestIdRef.current += 1;
      hasLoadedCurrentPageRef.current = false;
      loadedDraftPageKeyRef.current = null;
      publishedPageViewRef.current = {
        enhancedShapes: [],
        currentShape: null,
        isDrawing: false,
        loadedMeasurementSystem: 'metric',
        loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
      };
      publishedPageSnapshotRef.current = null;
      privateDraftSnapshotRef.current = null;
      persistedShapesByIdRef.current = new Map();
      draftStateByPageKeyRef.current.clear();
      clearScheduledDraftSave();
      shouldMigrateLegacyShapesRef.current = false;
      setShowDiscardDraft(false);
      setCurrentShape(null);
      setIsDrawing(false);
      setShapes([]);
      return;
    }

    // Get the actual version of the current page
    const currentPageVersion = currentPageData?.version;
    if (!currentPageVersion) {
      loadRequestIdRef.current += 1;
      hasLoadedCurrentPageRef.current = false;
      loadedDraftPageKeyRef.current = null;
      publishedPageViewRef.current = {
        enhancedShapes: [],
        currentShape: null,
        isDrawing: false,
        loadedMeasurementSystem: 'metric',
        loadedDisplayUnitSystem: null,
        loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
      };
      publishedPageSnapshotRef.current = null;
      privateDraftSnapshotRef.current = null;
      persistedShapesByIdRef.current = new Map();
      draftStateByPageKeyRef.current.clear();
      clearScheduledDraftSave();
      shouldMigrateLegacyShapesRef.current = false;
      setShowDiscardDraft(false);
      setCurrentShape(null);
      setIsDrawing(false);
      setShapes([]); // Clear shapes if no page version
      return;
    }

    const loadPageData = async () => {
      const requestId = loadRequestIdRef.current + 1;
      loadRequestIdRef.current = requestId;
      hasLoadedCurrentPageRef.current = false;
      loadedDraftPageKeyRef.current = null;

      try {
        setShowDiscardDraft(false);
        setDiscardDraftStatus('idle');

        const hydratePageData = (pageData) => {
          const transformShapeForCurrentDpi = (shape) => {
            if (!shape) {
              return null;
            }

            const shapeDPI =
              shape.storedDPI || shape.originalDPI || dynamicDPI;
            const scaleFactor = dynamicDPI / shapeDPI;

            if (scaleFactor === 1) {
              return shape;
            }

            const transformed = {
              ...shape,
              x:
                shape.x !== undefined
                  ? shape.x * scaleFactor
                  : shape.x,
              y:
                shape.y !== undefined
                  ? shape.y * scaleFactor
                  : shape.y,
              width:
                shape.width !== undefined
                  ? shape.width * scaleFactor
                  : shape.width,
              height:
                shape.height !== undefined
                  ? shape.height * scaleFactor
                  : shape.height,
              radius:
                shape.radius !== undefined
                  ? shape.radius * scaleFactor
                  : shape.radius,
              points: shape.points?.map((val) => val * scaleFactor),
              strokeWidth:
                shape.strokeWidth !== undefined
                  ? shape.strokeWidth * scaleFactor
                  : 4,
              holes: shape.holes?.map((hole) =>
                hole.map((val) => val * scaleFactor),
              ),
              originalDPI: shape.originalDPI || shapeDPI,
              storedDPI: dynamicDPI,
            };

            Object.keys(transformed).forEach((key) => {
              if (transformed[key] === undefined) {
                delete transformed[key];
              }
            });

            return transformed;
          };

          const sourceShapes = getOrderedShapesFromPageData(pageData);

          // Transform shapes if DPI differs from page DPI
          const transformedShapes = sourceShapes.map(
            transformShapeForCurrentDpi,
          );

          const deduplicated = deduplicateShapes(transformedShapes);

          const loadedMeasurementSystem =
            pageData?.measurementSystem || 'metric';
          const loadedDisplayUnitSystem = pageData?.displayUnitSystem || null;
          const loadedScaleKey =
            pageData?.selectedScaleKey ||
            getDefaultScaleKeyForMeasurementSystem(
              loadedMeasurementSystem,
            );

          // Build comprehensive scale list to support per-shape locked scales
          const allScales = [...METRIC_SCALES, ...IMPERIAL_SCALES, ...(customScales || [])];
          const loadedScaleProps = allScales.find(s => s.key === loadedScaleKey) ||
            (loadedMeasurementSystem === 'metric' ? METRIC_SCALES[0] : IMPERIAL_SCALES[0]);

          // Enhance shapes with correct scale props immediately if DPI and imageSize are available
          // If not available yet, the enhancement effect will handle it when they become available
          const shouldEnhance = dynamicDPI && dynamicDPI > 0 &&
            imageSize && imageSize.width > 0 && imageSize.height > 0;

          const enhancedShapes = shouldEnhance
            ? deduplicated.map((shape) => {
              const effectiveScaleProps = resolveShapeScaleProps(shape, loadedScaleProps, allScales);
              const effectiveScaleKey = shape.scaleKey || loadedScaleKey;
              const shapeCacheKey = `${effectiveScaleKey}_${dynamicDPI}_${loadedDisplayUnitSystem || 'auto'}`;
              const enhanced = enhanceShapeWithMeasurements(
                shape,
                effectiveScaleProps,
                dynamicDPI,
                imageSize,
                loadedDisplayUnitSystem
              );
              enhanced._measurementCacheKey = shapeCacheKey;
              return enhanced;
            })
            : deduplicated;

          const hydratedCurrentShape = pageData?.currentShape
            ? transformShapeForCurrentDpi(pageData.currentShape)
            : null;

          return {
            enhancedShapes,
            isDrawing: Boolean(pageData?.isDrawing && hydratedCurrentShape),
            loadedMeasurementSystem,
            loadedDisplayUnitSystem,
            loadedScaleKey,
          };
        };

        const organization = currentPageData?.organization;
        const pageIndexToUse = getPageIndexInVersion(currentPageData, currentPageVersion);
        console.log('DEBUG [loadPageData]', {
          currentImageIndex,
          pageIndexToUse,
          currentPageVersion,
          currentPageData,
        });
        // Use the actual page version, not the filter state
        const doc = await firebase.doGetPageData(
          organization,
          projectName,
          currentPageVersion,
          pageIndexToUse,
        );
        const publishedData = doc.exists ? doc.data() : null;
        const publishedShapesById =
          publishedData?.shapesById &&
            typeof publishedData.shapesById === 'object'
            ? publishedData.shapesById
            : null;

        // MIGRATION-CLEANUP: remove this assignment after rollout.
        shouldMigrateLegacyShapesRef.current = Boolean(
          publishedData && !publishedShapesById,
        );

        let privateDraftData = null;
        if (authUser?.uid) {
          privateDraftData = await firebase.doGetUserPreference(
            authUser.uid,
            buildReplanDraftPreferencePath(
              organization,
              projectName,
              currentPageVersion,
              pageIndexToUse,
            ),
          );
        }

        const publishedHydrated = publishedData
          ? hydratePageData(publishedData)
          : null;
        const privateDraftHydrated = privateDraftData
          ? hydratePageData(privateDraftData)
          : null;
        const publishedSnapshot = createComparablePageSnapshot(
          buildPageDraftPayload({
            shapes: publishedHydrated?.enhancedShapes || [],
            currentShape: null,
            isDrawing: false,
            measurementSystem:
              publishedHydrated?.loadedMeasurementSystem || 'metric',
            displayUnitSystem: publishedHydrated?.loadedDisplayUnitSystem || null,
            selectedScaleKey:
              publishedHydrated?.loadedScaleKey ||
              DEFAULT_METRIC_SCALE_KEY,
            dpiForSave: dynamicDPI || currentPageData?.dpi || 72,
          }),
        );

        const privateDraftSnapshot = createComparablePageSnapshot(
          buildPageDraftPayload({
            shapes: privateDraftHydrated?.enhancedShapes || [],
            currentShape: privateDraftHydrated?.currentShape || null,
            isDrawing: privateDraftHydrated?.isDrawing || false,
            measurementSystem:
              privateDraftData?.measurementSystem || 'metric',
            displayUnitSystem: privateDraftData?.displayUnitSystem || null,
            selectedScaleKey:
              privateDraftData?.selectedScaleKey ||
              getDefaultScaleKeyForMeasurementSystem(
                privateDraftData?.measurementSystem || 'metric',
              ),
            dpiForSave: dynamicDPI || currentPageData?.dpi || 72,
          }),
        );

        const publishedShapeCount =
          publishedSnapshot?.shapes?.length || 0;
        const privateDraftShapeCount =
          privateDraftSnapshot?.shapes?.length || 0;
        const privateDraftHasCurrentShape = Boolean(
          privateDraftSnapshot?.currentShape,
        );
        const isAmbiguousEmptyDraft = Boolean(
          privateDraftData &&
          publishedShapeCount > 0 &&
          privateDraftShapeCount === 0 &&
          !privateDraftHasCurrentShape,
        );

        const hasDifferentPersistedDraft = Boolean(
          privateDraftData &&
          !isAmbiguousEmptyDraft &&
          !arePageSnapshotsEquivalent(
            privateDraftSnapshot,
            publishedSnapshot,
          ),
        );

        const sourceHydrated = hasDifferentPersistedDraft
          ? privateDraftHydrated
          : publishedHydrated;
        const resolvedSourceHydrated = sourceHydrated || {
          enhancedShapes: [],
          currentShape: null,
          isDrawing: false,
          loadedMeasurementSystem: 'metric',
          loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
        };

        // Do not delete drafts during page load. A false negative in the
        // comparison logic would permanently erase the user's only private
        // work just by navigating to the page. Draft cleanup is safer after
        // the editor has settled and the autosave path can re-evaluate state.

        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const draftIndexPath = buildReplanDraftIndexPreferencePath(
          organization,
          projectName,
          currentPageVersion,
          currentImageIndex,
        );
        if (authUser?.uid) {
          if (hasDifferentPersistedDraft) {
            void firebase.doSetUserPreference(
              authUser.uid,
              draftIndexPath,
              { updatedAt: privateDraftData?.updatedAt || Date.now() },
            );
          } else {
            void firebase.doRemoveUserPreference(
              authUser.uid,
              draftIndexPath,
            );
          }
        }

        setMeasurementSystemRef.current(
          resolvedSourceHydrated.loadedMeasurementSystem,
        );
        setDisplayUnitSystem(resolvedSourceHydrated.loadedDisplayUnitSystem || null);
        setSelectedScaleKeyRef.current(resolvedSourceHydrated.loadedScaleKey);
        setCurrentShape(resolvedSourceHydrated.currentShape || null);
        setIsDrawing(Boolean(resolvedSourceHydrated.isDrawing));
        setShapesRef.current(resolvedSourceHydrated.enhancedShapes);

        persistedShapesByIdRef.current = new Map(
          (publishedHydrated?.enhancedShapes || [])
            .filter((shape) => shape?.id)
            .map((shape) => [shape.id, shape]),
        );
        publishedPageViewRef.current = publishedHydrated || {
          enhancedShapes: [],
          currentShape: null,
          isDrawing: false,
          loadedMeasurementSystem: 'metric',
          loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
        };

        publishedPageSnapshotRef.current = publishedSnapshot;
        privateDraftSnapshotRef.current = hasDifferentPersistedDraft
          ? privateDraftSnapshot
          : publishedSnapshot;

        loadedDraftPageKeyRef.current = getDraftStateKeyFromContext({
          organization,
          projectName,
          versionName: currentPageVersion,
          pageIndex: pageIndexToUse,
        });
        hasLoadedCurrentPageRef.current = true;
        setShowDiscardDraft(hasDifferentPersistedDraft);
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        console.error('Error loading page data:', error);
        hasLoadedCurrentPageRef.current = false;
        loadedDraftPageKeyRef.current = null;
        setShowDiscardDraft(false);
        setSaveStatus('error');
      }
    };

    loadPageData();
  }, [
    clearScheduledDraftSave,
    firebase,
    projectName,
    currentImageIndex,
    currentPageData,
    customScales, // Add customScales as it's used to determine scale props
    authUser?.uid,
    getPageIndexInVersion,
  ]);

  // Callback to SAVE page data to Firebase
  const handleSave = useCallback(async () => {
    if (
      !firebase ||
      !projectName ||
      typeof currentImageIndex !== 'number'
    ) {
      console.error('Cannot save, missing context:', {
        projectName,
        currentImageIndex,
      });
      setSaveStatus('error');
      return;
    }

    // Get the actual version of the current page
    const currentPageVersion = currentPageData?.version;
    if (!currentPageVersion) {
      console.error(
        'Cannot save, no page version found:',
        currentPageData,
      );
      setSaveStatus('error');
      return;
    }

    const pageIndexToUse = getPageIndexInVersion(currentPageData, currentPageVersion);

    setSaveStatus('saving');

    const dpiForSave = dynamicDPI || currentPageData?.dpi || 72;

    // De-dupe by ID (the UI already renders de-duped, but state can accumulate duplicates)
    // Filter out foreign shapes (from other versions) — they are read-only ephemeral
    const currentVersionShapes = shapes.filter((s) => !s.sourceVersion);
    const baseShapes = deduplicateShapes(currentVersionShapes);

    const persistedById = persistedShapesByIdRef.current || new Map();
    const currentById = new Map(
      baseShapes.filter((s) => s?.id).map((s) => [s.id, s]),
    );

    const changedOrNewShapeIds = [];
    currentById.forEach((shape, shapeId) => {
      const persistedShape = persistedById.get(shapeId);
      if (
        !persistedShape ||
        !areShapesEquivalentForSaveDiff(persistedShape, shape)
      ) {
        changedOrNewShapeIds.push(shapeId);
      }
    });

    // MIGRATION-CLEANUP: remove this block after rollout.
    // One-time migration: if page was loaded from legacy `shapes` array,
    // force-upsert all current shapes so Firestore stores `shapesById + shapeOrder`.
    if (shouldMigrateLegacyShapesRef.current) {
      changedOrNewShapeIds.length = 0;
      currentById.forEach((_, shapeId) => {
        changedOrNewShapeIds.push(shapeId);
      });
    }

    const deletedShapeIds = [];
    persistedById.forEach((_, shapeId) => {
      if (!currentById.has(shapeId)) {
        deletedShapeIds.push(shapeId);
      }
    });

    // Save-time metadata strategy:
    // - only changed/new shapes receive updatedBy/updatedAt
    // - createdBy/createdAt are backfilled if missing on changed/new shapes
    const currentUsername =
      authUser?.username ||
      authUser?.displayName ||
      authUser?.email ||
      authUser?.uid ||
      'unknown';
    const nowIso = new Date().toISOString();

    const upsertsById = {};
    changedOrNewShapeIds.forEach((shapeId) => {
      const currentShape = currentById.get(shapeId);
      if (!currentShape) return;

      const persistedShape = persistedById.get(shapeId);
      const migratedShape =
        !currentShape.originalDPI && dpiForSave
          ? { ...currentShape, originalDPI: dpiForSave }
          : currentShape;

      const hasCreationMetadata = Boolean(
        migratedShape?.createdByUsername && migratedShape?.createdAt,
      );
      const withMetadata = {
        ...migratedShape,
        createdByUsername: hasCreationMetadata
          ? migratedShape.createdByUsername
          : persistedShape?.createdByUsername ||
          migratedShape.updatedByUsername ||
          currentUsername,
        createdAt: hasCreationMetadata
          ? migratedShape.createdAt
          : persistedShape?.createdAt ||
          migratedShape.updatedAt ||
          nowIso,
        updatedByUsername: currentUsername,
        updatedAt: nowIso,
      };

      // Note: sanitizeShapeForFirestore strips measurement fields
      upsertsById[shapeId] = sanitizeShapeForFirestore(withMetadata);
    });

    const pageFields = {
      measurementSystem: measurementSystem,
      displayUnitSystem: displayUnitSystem || null,
      selectedScaleKey: selectedScaleKey,
      referenceWidth: imageSize.width,
      referenceHeight: imageSize.height,
    };

    try {
      // Get organization from current page data
      const organization = currentPageData?.organization;
      if (!organization) {
        console.error(
          'Cannot save: no organization found in currentPageData',
        );
        setSaveStatus('error');
        return;
      }
      // Compact shapes: extract page-level defaults, shorten field names, strip id
      const shapeDefaults = extractShapeDefaults(baseShapes.filter(s => s?.id));
      const prevDefaults = currentPageData?.shapeDefaults || {};

      // If defaults changed, rewrite all shapes to prevent stale data loss
      const defaultsChanged =
        Object.keys(prevDefaults).length !== Object.keys(shapeDefaults).length ||
        Object.entries(shapeDefaults).some(([k, v]) => prevDefaults[k] !== v);

      const finalUpserts = defaultsChanged
        ? Object.fromEntries(
          baseShapes.filter(s => s?.id).map(s => [
            s.id,
            compactShape(
              upsertsById[s.id] || sanitizeShapeForFirestore(persistedById.get(s.id) || s),
              shapeDefaults,
            ),
          ]),
        )
        : Object.fromEntries(
          Object.entries(upsertsById).map(([id, shape]) => [id, compactShape(shape, shapeDefaults)]),
        );

      clearScheduledDraftSave();
      const fieldsToDelete = shouldMigrateLegacyShapesRef.current
        ? ['shapes']
        : [];
      await firebase.doPatchPageData(
        organization,
        projectName,
        currentPageVersion,
        pageIndexToUse,
        {
          upserts: finalUpserts,
          deletedIds: deletedShapeIds,
          shapeOrder: baseShapes
            .filter((s) => s?.id)
            .map((s) => s.id),
          pageFields: {
            ...pageFields,
            shapeDefaults,
          },
          fieldsToDelete,
          actorUid: authUser?.uid || null,
          actorEmail: authUser?.email || null,
        },
      );

      await updateAssemblyMeasurements(
        firebase,
        organization,
        projectName,
        currentPageVersion,
        pageIndexToUse,
        baseShapes,
        persistedShapesByIdRef.current || new Map(),
        selectedScaleKey,
      );

      const draftPath = buildReplanDraftPreferencePath(
        organization,
        projectName,
        currentPageVersion,
        pageIndexToUse,
      );
      const draftIndexPath = buildReplanDraftIndexPreferencePath(
        organization,
        projectName,
        currentPageVersion,
        pageIndexToUse,
      );
      const draftCleanupResults = await Promise.allSettled([
        firebase.doRemoveUserPreference(authUser.uid, draftPath),
        firebase.doRemoveUserPreference(authUser.uid, draftIndexPath),
      ]);

      draftCleanupResults.forEach((result, cleanupIndex) => {
        if (result.status === 'rejected') {
          console.warn(
            cleanupIndex === 0
              ? 'Published Replan page saved, but draft payload cleanup failed:'
              : 'Published Replan page saved, but draft index cleanup failed:',
            result.reason,
          );
        }
      });

      setPagesWithShapes((currentPagesWithShapes) => {
        const nextPagesWithShapes = new Map(currentPagesWithShapes);
        const versionPages = new Set(
          nextPagesWithShapes.get(currentPageVersion) || [],
        );

        if (baseShapes.length > 0) {
          versionPages.add(pageIndexToUse);
          nextPagesWithShapes.set(currentPageVersion, versionPages);
        } else {
          versionPages.delete(pageIndexToUse);
          if (versionPages.size > 0) {
            nextPagesWithShapes.set(currentPageVersion, versionPages);
          } else {
            nextPagesWithShapes.delete(currentPageVersion);
          }
        }

        return nextPagesWithShapes;
      });

      // Refresh local persisted baseline after successful save.
      const nextPersisted = new Map(persistedById);
      Object.entries(upsertsById).forEach(([shapeId, shapeData]) => {
        nextPersisted.set(shapeId, shapeData);
      });
      deletedShapeIds.forEach((shapeId) => {
        nextPersisted.delete(shapeId);
      });
      persistedShapesByIdRef.current = nextPersisted;
      shouldMigrateLegacyShapesRef.current = false;
      publishedPageViewRef.current = {
        enhancedShapes: baseShapes,
        currentShape: null,
        isDrawing: false,
        loadedMeasurementSystem: measurementSystem,
        loadedScaleKey: selectedScaleKey,
      };
      const currentSnapshot = createComparablePageSnapshot(
        buildPageDraftPayload({
          shapes: baseShapes,
          currentShape: null,
          isDrawing: false,
          measurementSystem,
          selectedScaleKey,
          displayUnitSystem: displayUnitSystem || null,
          dpiForSave,
        }),
      );
      publishedPageSnapshotRef.current = currentSnapshot;
      privateDraftSnapshotRef.current = currentSnapshot;
      draftStateByPageKeyRef.current.set(
        buildReplanDraftStateKey(
          organization,
          projectName,
          currentPageVersion,
          pageIndexToUse,
        ),
        {
          authUid: authUser.uid,
          organization,
          projectName,
          version: currentPageVersion,
          pageIndex: pageIndexToUse,
          pageKey: buildReplanDraftStateKey(
            organization,
            projectName,
            currentPageVersion,
            pageIndexToUse,
          ),
          payload: buildPageDraftPayload({
            shapes: baseShapes,
            currentShape: null,
            isDrawing: false,
            measurementSystem,
            selectedScaleKey,
            displayUnitSystem: displayUnitSystem || null,
            dpiForSave,
          }),
          snapshot: currentSnapshot,
          publishedSnapshot: currentSnapshot,
        },
      );
      setShowDiscardDraft(false);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving page data:', error);
      setSaveStatus('error');
    }
  }, [
    clearScheduledDraftSave,
    firebase,
    projectName,
    currentImageIndex,
    currentPageData,
    shapes,
    measurementSystem,
    displayUnitSystem,
    selectedScaleKey,
    dynamicDPI,
    imageSize.width,
    imageSize.height,
    currentScaleProps,
    imageSize,
    getPageIndexInVersion,
  ]);

  const handleDiscardDraft = useCallback(async () => {
    const draftContext = getCurrentDraftContext();
    if (!draftContext || !authUser?.uid || !firebase) {
      return;
    }

    setDiscardDraftStatus('deleting');

    try {
      clearScheduledDraftSave();
      await firebase.doRemoveUserPreference(
        authUser.uid,
        buildReplanDraftPreferencePath(
          draftContext.organization,
          projectName,
          draftContext.version,
          draftContext.pageIndex,
        ),
      );
      await firebase.doRemoveUserPreference(
        authUser.uid,
        buildReplanDraftIndexPreferencePath(
          draftContext.organization,
          projectName,
          draftContext.version,
          draftContext.pageIndex,
        ),
      );

      const publishedView = publishedPageViewRef.current || {
        enhancedShapes: [],
        currentShape: null,
        isDrawing: false,
        loadedMeasurementSystem: 'metric',
        loadedScaleKey: DEFAULT_METRIC_SCALE_KEY,
      };

      setSelectedShapeIds([]);
      setMeasurementSystem(publishedView.loadedMeasurementSystem);
      setSelectedScaleKey(publishedView.loadedScaleKey);
      setCurrentShape(publishedView.currentShape || null);
      setIsDrawing(Boolean(publishedView.isDrawing));
      setShapes(publishedView.enhancedShapes);
      privateDraftSnapshotRef.current = publishedPageSnapshotRef.current;
      draftStateByPageKeyRef.current.delete(
        buildReplanDraftStateKey(
          draftContext.organization,
          projectName,
          draftContext.version,
          draftContext.pageIndex,
        ),
      );
      setShowDiscardDraft(false);
      setDiscardDraftStatus('idle');
    } catch (error) {
      console.error('Error discarding Replan draft:', error);
      setDiscardDraftStatus('error');
    }
  }, [
    authUser?.uid,
    clearScheduledDraftSave,
    firebase,
    getCurrentDraftContext,
    projectName,
    setMeasurementSystem,
    setSelectedScaleKey,
    setSelectedShapeIds,
    setShapes,
  ]);

  // Handler for printing
  const handlePrint = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Find the background rectangle and hide it
    const backgroundRect = stage.findOne('.background-rect');
    const originalVisible = backgroundRect
      ? backgroundRect.visible()
      : true;

    if (backgroundRect) {
      backgroundRect.visible(false);
    }

    // Convert stage to high-res image
    // Using a pixelRatio of 3 ensures better quality on print (300dpi approximation)
    const dataUrl = stage.toDataURL({ pixelRatio: 3 });

    // Restore visibility
    if (backgroundRect) {
      backgroundRect.visible(originalVisible);
    }

    // Detect orientation
    const width = stage.width();
    const height = stage.height();
    const isLandscape = width > height;
    const pageOrientation = isLandscape ? 'landscape' : 'portrait';

    // Create a new window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            
            <style>
              @media print {
                @page { size: ${pageOrientation}; margin: 0; }
                body { margin: 0; }
              }
              html, body {
                margin: 0;
                padding: 0;
                width: 100%;
                height: auto;
                overflow: hidden;
              }
              body {
                display: flex;
                justify-content: center;
                align-items: center;
              }
              img {
                width: auto;
                height: auto;
                object-fit: cover;
              }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" onload="window.print(); setTimeout(function(){window.close();}, 500);" />
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  }, [projectName]);

  // ---logic to determine when to show the area mini-bar ---
  const selectedPolygonCount = useMemo(() => {
    return shapes.filter(
      (shape) =>
        selectedShapeIds.includes(shape.id) &&
        (shape.type === TOOLS.AREA),
    ).length;
  }, [shapes, selectedShapeIds]);

  const showBooleanOperations =
    selectedPolygonCount >= 2 && selectedTool === TOOLS.SELECT;

  // Insert-vertex mode is available when exactly one AREA/POLYLINE shape is selected
  const canInsertVertex =
    selectedShapeIds.length === 1 &&
    shapes.some(
      (s) =>
        s.id === selectedShapeIds[0] &&
        (s.type === TOOLS.AREA || s.type === TOOLS.POLYLINE),
    );

  // Effect to manage the Contextual Tool Panel's position and visibility
  useEffect(() => {
    const showPanel =
      selectedShapeIds.length > 0 && selectedTool === TOOLS.SELECT;

    if (showPanel) {
      const stage = stageRef.current;
      if (!stage) return;

      // Get the last known pointer position on the stage
      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) {
        // Fallback if no pointer position is available (e.g., selection from list)
        setContextualPanelVisible(false); // Or hide it
        return;
      }

      // The pointer position is relative to the container, which is what we need.
      setContextualPanelPos({ x: pointerPos.x, y: pointerPos.y });
      setContextualPanelVisible(true);
    } else {
      // Hide the panel if the condition is not met
      setContextualPanelVisible(false);
    }
  }, [selectedShapeIds, selectedTool]); // Reruns when the selection or tool changes

  // --- Page Reference Hover Preview Logic Handlers ---
  const handleReferenceMouseEnter = useCallback((ref, e) => {
    // Clear any existing timer
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    setIsHoveringPageRef(true);

    const stage = e.target.getStage();

    // Update mousePosRef right now just in case they don't move the mouse
    const pointerPos = stage.getPointerPosition();
    if (pointerPos) {
      mousePosRef.current = {
        x: pointerPos.x + 20,
        y: pointerPos.y + 20,
        clientX: e.evt.clientX,
        clientY: e.evt.clientY
      };
    }

    // Start 1-second delay timer
    hoverTimerRef.current = setTimeout(() => {
      setPreviewData({
        targetPageLabel: ref.targetPage,
        position: mousePosRef.current
      });
    }, 1000);
  }, []);

  const handleReferenceMouseMove = useCallback((e) => {
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    if (pointerPos) {
      mousePosRef.current = {
        x: pointerPos.x + 20,
        y: pointerPos.y + 20,
        clientX: e.evt.clientX,
        clientY: e.evt.clientY
      };
    }
  }, []);

  const handleReferenceMouseLeave = useCallback((e) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    // Reset state
    setPreviewData(null);
    setIsHoveringPageRef(false);
  }, []);

  // Clear preview state whenever the page changes to avoid "ghost" previews
  useEffect(() => {
    setPreviewData(null);
    setIsHoveringPageRef(false);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, [currentImageIndex]);

  // Wrapper for navigation that clears preview state immediately
  // Wrapper for navigation that implements "click to preview, click again to navigate" logic
  const handleNavigateToPageReference = useCallback((targetPage, ref, e) => {
    // If the preview is already visible for this specific page, navigate as usual
    if (previewData && previewData.targetPageLabel === targetPage) {
      setPreviewData(null);
      setIsHoveringPageRef(false);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

      // Manual reset when clicking a page link (not sequential arrows)
      resetView();
      navigateToPageReference(targetPage);
      return;
    }

    // Otherwise, if the preview is NOT visible, make it appear immediately
    // Cancel any pending 1s timer to avoid double-triggers
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    if (pointerPos) {
      setPreviewData({
        targetPageLabel: targetPage,
        position: {
          x: pointerPos.x + 20,
          y: pointerPos.y + 20,
          clientX: e.evt.clientX,
          clientY: e.evt.clientY
        }
      });
    }
  }, [previewData, navigateToPageReference, resetView]);

  // --- Effects for area tool panel ---
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Close the panel if clicking outside of it
      if (
        contextualPanelVisible &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setContextualPanelVisible(false);
      }
    };

    // Attach the listener to the document
    document.addEventListener('click', handleGlobalClick);

    // Cleanup the listener on unmount or when dependencies change
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [contextualPanelVisible]);

  /**
   * Auto-center view on current search match
   * Smoothly pans the stage to center the currently selected search result
   * Uses Konva's animation for smooth transitions
   */
  useEffect(() => {
    if (
      searchMatches.length > 0 &&
      stageRef.current &&
      containerRef.current
    ) {
      const match = searchMatches[currentSearchIndex];
      const pos = match.position || match; // Extract position from the match object
      const stage = stageRef.current;

      // Calculate the center point of the match bounding box
      const matchCenterX = pos.x + pos.width / 2;
      const matchCenterY = pos.y + pos.height / 2;

      // Calculate the position to center this point in the viewport
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      const newX = containerWidth / 2 - matchCenterX * safeStageScale;
      const newY =
        containerHeight / 2 - matchCenterY * safeStageScale;

      // Smoothly animate to the new position (300ms duration)
      stage.to({
        x: newX,
        y: newY,
        duration: 0.3,
      });
    }
  }, [currentSearchIndex, searchMatches, safeStageScale]);

  // --- Text Editing Handlers ---
  const handleStartTextEdit = useCallback((shapeId, textRef) => {
    setTextEditingState({ shapeId, textRef });
    setTimeout(() => selectTool(TOOLS.SELECT), 0);
  }, [selectTool]);

  const handleTextChange = useCallback(
    (newText) => {
      if (textEditingState) {
        handleUpdateShape(textEditingState.shapeId, {
          text: newText,
        });
        setTextEditingState(null);
      }
    },
    [textEditingState, handleUpdateShape],
  );

  // --- Measurement Summary Handlers ---
  const handleCreateMeasurementSummary = useCallback(() => {
    if (!selectedShapeIds || selectedShapeIds.length === 0) {
      alert('Please select shapes to create a measurement summary.');
      return;
    }

    // Create a measurement summary shape
    const newShape = {
      id: generateId(),
      type: TOOLS.MEASUREMENT_SUMMARY,
      x: 50,
      y: 50,
      trackedShapeIds: [...selectedShapeIds],
      groupingKey: groupingKey,
      width: 280,
      visible: true,
      color: '#3B82F6',
      params: {},
      createdAt: new Date().toISOString(),
      createdByUsername: authUser?.username || 'Unknown',
    };

    setShapes(prev => [...prev, newShape]);
  }, [selectedShapeIds, groupingKey, setShapes, authUser]);

  useEffect(() => {
    handleCreateMeasurementSummaryRef.current = handleCreateMeasurementSummary;
  }, [handleCreateMeasurementSummary]);

  const overlayContentJSX = (overlayImage || overlayPdfBase) && !isLoading && !error ? (
    <Group>
      {overlayPdfBase ? (
        <>
          <KonvaImage
            image={overlayPdfBase}
            width={imageSize.width}
            height={imageSize.height}
            perfectDrawEnabled={false}
            opacity={0.9}
            listening={false}
          />
          {overlayPdfHighRes && overlayPdfHighResBounds && (
            <KonvaImage
              image={overlayPdfHighRes}
              x={overlayPdfHighResBounds.x}
              y={overlayPdfHighResBounds.y}
              width={overlayPdfHighResBounds.width}
              height={overlayPdfHighResBounds.height}
              perfectDrawEnabled={false}
              opacity={0.9}
              listening={false}
            />
          )}
        </>
      ) : (
        <KonvaImage
          name="overlay-image"
          image={overlayImage}
          width={imageSize.width}
          height={imageSize.height}
          opacity={0.9}
          listening={false}
          ref={overlayImageRef}
          filters={overlayFilterEnabled ? [Konva.Filters.RGBA] : []}
          red={overlayFilterEnabled ? overlayTintRgb.r : 255}
          green={overlayFilterEnabled ? overlayTintRgb.g : 255}
          blue={overlayFilterEnabled ? overlayTintRgb.b : 255}
          alpha={1}
        />
      )}
    </Group>
  ) : null;

  const preDrawRef = useRef({ strokeColor: null, strokeWidth: null });

  const handleDrawSameShape = useCallback((shape) => {
    preDrawRef.current = { strokeColor, strokeWidth };
    if (shape.type === TOOLS.POINT) {
      setSelectedPointMarker(shape.pointMarker || 'disk');
    }
    if ([TOOLS.TEXT, TOOLS.RECTANGLE, TOOLS.ARROW].includes(shape.type)) {
      setSelectedAnnotationTool(shape.type);
    }
    handleStrokeColorChange(shape.stroke || strokeColor);
    handleStrokeWidthChange(shape.strokeWidth || strokeWidth);
    selectTool(shape.type);
  }, [selectTool, handleStrokeColorChange, handleStrokeWidthChange, strokeColor, strokeWidth, setSelectedPointMarker, setSelectedAnnotationTool]);

  const handleRestoreDrawSettings = useCallback(() => {
    const prev = preDrawRef.current;
    if (prev.strokeColor) handleStrokeColorChange(prev.strokeColor);
    if (prev.strokeWidth) handleStrokeWidthChange(prev.strokeWidth);
  }, [handleStrokeColorChange, handleStrokeWidthChange]);

  // --- Render ---
  return (
    <div className="whiteboard-container" ref={containerRef}>
      {/* PDF Controls */}
      <PdfControls
        pdfDoc={konvaImage}
        stageScale={safeStageScale} // Use safeStageScale
        currentPage={currentImageIndex + 1}
        numPages={numImages}
        pages={pages} // Pass the new pages array down
        onPageChange={handlePageChange} // Pass the new handler
        onResetView={resetView}
        mousePos={mousePos} // Pass the state down
        // New props for project/version selection
        projectName={projectName}
        versionName={versionName}
        onProjectChange={setProjectNameSynced}
        onVersionChange={handleVersionChange}
        selectedFolder={selectedFolder}
        onFolderChange={handleFolderChange}
        measurementSystem={measurementSystem}
        onMeasurementSystemChange={setMeasurementSystem}
        selectedScaleKey={selectedScaleKey}
        onSelectedScaleKeyChange={setSelectedScaleKey}
        // Pass save handlers
        onSave={handleSave}
        saveStatus={saveStatus}
        onDiscardDraft={handleDiscardDraft}
        showDiscardDraft={showDiscardDraft}
        discardDraftStatus={discardDraftStatus}
        onPrint={handlePrint}
        authUser={authUser} // Pass authUser to PdfControls
        firebase={firebase}
        customScales={customScales}
        setCustomScales={setCustomScales}
        // UndoRedo bar
        canUndo={canUndo}
        canRedo={canRedo}
        undo={undo}
        redo={redo}
        onSearchResults={handleSearchResults}
        pagesWithShapes={pagesWithShapes}
        displayKeys={displayKeys}
        displayUnitSystem={displayUnitSystem}
        overlayTintColor={overlayTintColor}
        onOverlayTintChange={setOverlayTintColor}
        overlayModeEnabled={overlayModeEnabled}
        onOverlayModeChange={setOverlayModeEnabled}
        overlayFilterEnabled={overlayFilterEnabled}
        onOverlayFilterChange={setOverlayFilterEnabled}
        isOverlayOnTop={isOverlayOnTop}
        onOverlayOrderChange={setIsOverlayOnTop}
        selectedVersionShapes={selectedVersionShapes}
        onVersionShapesChange={setSelectedVersionShapes}
        onSavedPageRestoreComplete={handleSavedPageRestoreComplete}
        hasDocuments={projectHasDocuments}
      />
      {/* Toolbar */}
      {/*projectHasDocuments*/ true && (
        <Toolbar
          selectedTool={selectedTool}
          onSelectTool={selectTool}
          strokeColor={strokeColor}
          onColorChange={handleStrokeColorChange}
          strokeWidth={strokeWidth}
          onStrokeWidthChange={handleStrokeWidthChange}
          isOrthogonalModeActive={isOrthogonalModeActive}
          setIsOrthogonalModeActive={setIsOrthogonalModeActive}
          isRectangleMode={isRectangleMode}
          setIsRectangleMode={setIsRectangleMode}
          isDraggingMode={isDraggingMode}
          setIsDraggingMode={setIsDraggingMode}
          isSnappingEnabled={isSnappingEnabled}
          setIsSnappingEnabled={setIsSnappingEnabled}
          isGridVisible={isGridVisible}
          setIsGridVisible={setIsGridVisible}
          onCreateMeasurementSummary={handleCreateMeasurementSummary}
          selectedPointMarker={selectedPointMarker}
          onSelectPointMarker={setSelectedPointMarker}
          selectedAnnotationTool={selectedAnnotationTool}
          onSelectAnnotationTool={setSelectedAnnotationTool}
          onBlockScale={handleBlockScale}
          canUnblock={allSelectedBlockedAtCurrentScale}
        />

      )}

      {/* Contextual Operations Mini-bar (Boolean + Arrange + Scale Blocking) */}
      <ContextualToolPanel
        onExecuteOperation={executeBooleanOperation}
        onArrange={handleArrange}
        onTransform={handleTransform}
        selectedPolygonCount={selectedPolygonCount}
        selectionCount={selectedShapeIds.length}
        position={contextualPanelPos}
        visible={contextualPanelVisible}
        transformMode={transformMode}
        insertVertexMode={insertVertexMode}
        onToggleInsertVertex={() => setInsertVertexMode(v => !v)}
        canInsertVertex={canInsertVertex}
      />

      {/* Transform Mode Indicator */}
      {/* we will refactor this later to work also with drawing mode */}
      {/* {isTransformActive && (
        <div className="transform-mode-indicator">
          <span>
            {transformMode === 'move' && !transformPreview && 'Click base point to move from'}
            {transformMode === 'move' && transformPreview && 'Click destination to move'}
            {transformMode === 'copy' && !transformPreview && 'Click base point to copy from'}
            {transformMode === 'copy' && transformPreview && 'Click to place copies (repeat)'}
            {transformMode === 'symmetry' && !transformPreview?.line && 'Click first point of axis'}
            {transformMode === 'symmetry' && transformPreview?.line && 'Click second point of axis'}
            {transformMode === 'rotate' && !transformPreview?.line && 'Click to set pivot'}
            {transformMode === 'rotate' && transformPreview?.line && `Click to rotate ${transformPreview.label || ''}`}
            {transformMode === 'scale' && !transformPreview?.line && 'Click to set pivot'}
            {transformMode === 'scale' && transformPreview?.line && `Click to scale ${transformPreview.label || ''}`}
          </span>
          <button className="transform-cancel-btn" onClick={cancelTransform}>
            ESC
          </button>
        </div>
      )} */}

      {/* 
          MINIMALIST LOADER 
          Displays status messages while the PDF is fetching/rendering the first base layer.
          Once basePdfImage exists, we switch to Konva Stage for the responsive experience.
      */}
      {isLoading && (
        <div className="streaming-loader-container">
          <div className="streaming-loader-spinner" />
          <div className="streaming-status-message">
            {/* {loadingStatus || 'Preparing drawing...'} */}
          </div>
        </div>
      )}

      {/* PDF rendering status indicator — shows during base render and high-res patches */}
      {(isRenderingHighRes || (isPdfLoading && pdfStreamTriggered && konvaImage)) && (
        <div className="hires-enhancing-indicator">
          <div className="hires-enhancing-dot" />
          <span>{isPdfLoading && !basePdfImage ? t('replan.pdf_rendering') : t('replan.enhancing')}</span>
        </div>
      )}

      {/* Overlay loading indicator — shows while the comparison version image is being fetched/decoded */}
      {isOverlayLoading && (
        <div className="hires-enhancing-indicator">
          <div className="hires-enhancing-dot" />
          <span>{t('replan.preparing_overlay')}</span>
        </div>
      )}

      <React.Suspense
        fallback={createPortal(
          <div className="upload-plans-modal-overlay">
            <div
              className="upload-plans-modal"
              role="dialog"
              aria-busy="true"
            >
              <div className="upload-plans-modal__body">
                <div className="replan-lazy-fallback">
                  {t('replan.loading_upload', {
                    defaultValue: 'Loading upload…',
                  })}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
      >
        <UploadPlansModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          projectName={projectName}
          organisation={uploadOrganisation}
          authUser={authUser}
          firebase={firebase}
          onProjectListChanged={refreshProjects}
        />
      </React.Suspense>

      {/* Konva Stage - always rendered. Without a document it shows a blank
          A0 sheet on the grid, with live pan/zoom like a CAD viewport. */}
      {containerSize.width > 0 &&
        containerSize.height > 0 &&
        !isLoading && (
        <Stage
          ref={stageRef}
          width={containerSize.width} // Adjust width to make space for list
          height={containerSize.height}
          onMouseDown={(e) => {
            // Guard against synthesized mouse events that browsers fire after touch.
            // pointerType==='touch' works on Chrome/Android but NOT on iOS Safari
            // (which emits a plain MouseEvent with no pointerType). The timestamp
            // check covers both: any mousedown within 500 ms of a touchstart is skipped.
            const isSynthesizedFromTouch =
              e.evt.pointerType === 'touch' ||
              Date.now() - lastTouchTimeRef.current < 500;
            if (isSynthesizedFromTouch) return;
            if (isTransformActive && handleTransformPointerDown(e)) return;
            handlePointerDown(e);
          }}
          onMouseMove={(e) => {
            const isSynthesizedFromTouch =
              e.evt.pointerType === 'touch' ||
              Date.now() - lastTouchTimeRef.current < 500;
            if (isSynthesizedFromTouch) return;
            handleStageMouseMove(e);
          }}
          onMouseUp={(e) => {
            const isSynthesizedFromTouch =
              e.evt.pointerType === 'touch' ||
              Date.now() - lastTouchTimeRef.current < 500;
            if (isSynthesizedFromTouch) return;
            if (isTransformActive) return;
            handlePointerUp(e);
          }}
          onMouseLeave={handleStageMouseLeave}
          onDblClick={handleDoubleClick}
          onDblTap={handleDoubleClick}
          onWheel={(e) => {
            startInteraction();
            handleWheel(e);
            stopInteraction();
          }}
          onTouchStart={(e) => {
            // Stamp time so the ensuing synthesized mouse events can be suppressed.
            lastTouchTimeRef.current = Date.now();
            startInteraction();
            handleUnifiedPointerDown(e);
          }}
          onTouchMove={handleUnifiedPointerMove}
          onTouchEnd={(e) => {
            handleUnifiedPointerUp(e);
            stopInteraction();
          }}
          scaleX={safeStageScale}
          scaleY={safeStageScale} // Use safeStageScale
          x={stagePos.x}
          y={stagePos.y} // Draggable with SELECT (if not drawing marquee) OR with NAVIGATION tool
          draggable={
            !isTransformActive &&
            ((selectedTool === TOOLS.SELECT && !isDrawing) ||
              selectedTool === TOOLS.NAVIGATION)
          }
          onContextMenu={(e) => e.evt.preventDefault()} // Prevent browser context menu on stage
          onDragStart={handleStageDragStart}
          onDragEnd={handleStageDragEndCombined}
        // style={{ borderRight: '1px solid #ccc' }} // Optional separator
        >
          {/* 1. Explicit Background Layer (Provides the requested theme fill color) */}
          <Layer name="background-layer" listening={false}>
            {konvaImage && (
              <KonvaRect
                name="background-rect"
                x={0}
                y={0}
                width={imageSize.width}
                height={imageSize.height}
                fill={getComputedStyle(document.documentElement)
                  .getPropertyValue('--drawing-background-color')
                  .trim()}
                listening={false}
              />
            )}
          </Layer>

          {/* 2. PDF Image Layer (Blended via CSS mix-blend-mode) */}
          <Layer name="image-layer" listening={false}>
            {konvaImage && (
              <>
                {/* Background rendering order logic */}
                {isOverlayOnTop ? null : overlayContentJSX}

                {/* Base background plan group */}
                <Group>
                  {true && (
                    <KonvaImage
                      name="background-image"
                      image={konvaImage}
                      width={imageSize.width}
                      height={imageSize.height}
                      listening={false} // Make image non-interactive for clicks
                      perfectDrawEnabled={false} // Disable to prevent double-buffering coordinate drift
                      opacity={overlayModeEnabled ? 0.9 : 1} // Always keep base layer visible for fluid motion; the opaque high-res patch covers it when stopped.
                    />
                  )}
                  {/* The Surgical High-Res Zoom Overlay */}
                  {basePdfImage && highResImage && highResBounds && (
                    <KonvaImage
                      name="high-res-overlay"
                      image={highResImage}
                      x={highResBounds.x}
                      y={highResBounds.y}
                      width={highResBounds.width}
                      height={highResBounds.height}
                      listening={false}
                      perfectDrawEnabled={false} // Match base layer to ensure perfect pixel alignment
                    />
                  )}
                </Group>

                {isOverlayOnTop ? overlayContentJSX : null}
              </>
            )}
            {error && (
              <KonvaText
                x={imageSize.width / 2 - 100}
                y={imageSize.height / 2 - 10}
                text={`Error: ${error}`}
                fontSize={20}
                fill="red"
                listening={false}
              />
            )}
            {/* Empty project: blank A0 sheet at world origin (0,0)..(W,H) so the
                initial fit centers it exactly like the plan image. */}
            {!projectHasDocuments && (
              <KonvaRect
                name="empty-sheet"
                x={0}
                y={0}
                width={EMPTY_SHEET_WIDTH}
                height={EMPTY_SHEET_HEIGHT}
                fill={emptySheetColors.sheet}
                stroke={emptySheetColors.border}
                strokeWidth={1 / safeStageScale}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
            {/* Grid layer - rendered on TOP of the plan but below shapes.
                Auto-visible on the empty sheet; otherwise follows the toggle. */}
            {(isGridVisible || !projectHasDocuments) && (
              <Grid
                currentScaleProps={currentScaleProps}
                dpi={dynamicDPI}
                stageScale={safeStageScale}
                stagePos={stagePos}
                containerSize={containerSize}
                measurementSystem={measurementSystem}
              />
            )}
          </Layer>

            {/* Empty-project message: fixed on the sheet, constant screen size,
                clickable to open the upload modal. The action line shows a
                pointer cursor + tooltip and its hit area hugs the text. */}
            {!projectHasDocuments && (
              <Layer name="empty-state-layer">
                <Group
                  onClick={() => setIsUploadModalOpen(true)}
                  onTap={() => setIsUploadModalOpen(true)}
                  listening
                >
                  <KonvaText
                    ref={emptyTitleRef}
                    x={(EMPTY_SHEET_WIDTH - (emptyTextWidths.title || EMPTY_SHEET_WIDTH * 0.7)) / 2}
                    y={EMPTY_SHEET_HEIGHT * 0.24}
                    width={emptyTextWidths.title || EMPTY_SHEET_WIDTH * 0.7}
                    text={emptyTitleText}
                    fontSize={30 * 6}
                    fontStyle="bold"
                    align="center"
                    fill={emptySheetColors.ink}
                    perfectDrawEnabled={false}
                  />
                  <KonvaText
                    x={EMPTY_SHEET_WIDTH * 0.15}
                    y={EMPTY_SHEET_HEIGHT * 0.3}
                    width={EMPTY_SHEET_WIDTH * 0.7}
                    text={t('replan.no_plans_message', {
                      defaultValue:
                        'This project has no uploaded documents. Upload a plan to start measuring and annotating.',
                    })}
                    fontSize={18 * 6}
                    align="center"
                    wrap="word"
                    fill={emptySheetColors.ink}
                    perfectDrawEnabled={false}
                  />
                  <KonvaText
                    ref={emptyActionRef}
                    x={emptyRowX}
                    y={emptyRowY}
                    width={emptyActionW}
                    text={emptyActionText}
                    fontSize={19 * 6}
                    fontStyle="bold"
                    align="center"
                    fill={emptySheetColors.accent}
                    textDecoration="underline"
                    cursor="pointer"
                    perfectDrawEnabled={false}
                    onMouseEnter={(e) => {
                      setEmptyLinkHovered(true);
                      handleEmptyTooltip(e, emptyUploadHintText);
                    }}
                    onMouseMove={(e) => handleEmptyTooltip(e, emptyUploadHintText)}
                    onMouseLeave={() => {
                      setEmptyLinkHovered(false);
                      setEmptyTooltip(null);
                    }}
                  />
                  {/* VControl link: same row/look as the upload link */}
                  <KonvaText
                    ref={emptyVControlRef}
                    x={emptyRowX + emptyActionW + emptyLinkGap}
                    y={emptyRowY}
                    width={emptyVcW}
                    text={emptyVControlText}
                    fontSize={19 * 6}
                    fontStyle="bold"
                    align="center"
                    fill={emptySheetColors.accent}
                    textDecoration="underline"
                    cursor="pointer"
                    perfectDrawEnabled={false}
                    onMouseEnter={(e) => {
                      setEmptyLinkHovered(true);
                      handleEmptyTooltip(e, emptyVControlHintText);
                    }}
                    onMouseMove={(e) => handleEmptyTooltip(e, emptyVControlHintText)}
                    onMouseLeave={() => {
                      setEmptyLinkHovered(false);
                      setEmptyTooltip(null);
                    }}
                    onClick={(e) => {
                      e.cancelBubble = true;
                      navigate(VCONTROL);
                    }}
                    onTap={(e) => {
                      e.cancelBubble = true;
                      navigate(VCONTROL);
                    }}
                  />
                </Group>
              </Layer>
            )}


          {/* Drawing Layer */}
          <Layer name="drawing-layer" ref={drawingLayerRef}>
            {/* Locked scale overlay rectangles */}
            <LockedScaleOverlay
              allShapes={shapes}
              allScales={allScales}
              stageScale={safeStageScale}
              selectedShapeIds={selectedShapeIds}
              onSelectShapes={(ids) => handleShapeListItemSelect(ids, true)}
            />
            {[...deduplicatedShapes]
              .filter((shape) => shape && shape.id && shape.type)
              .sort((a, b) => {
                const aSel = selectedSet.has(a.id);
                const bSel = selectedSet.has(b.id);
                if (aSel && !bSel) return 1;
                if (!aSel && bSel) return -1;
                return 0;
              })
              .map((shape) => (
                <ShapeRenderer
                  key={shape.id}
                  shape={shape}
                  isSelected={selectedSet.has(shape.id)}
                  selectedShapeIds={selectedShapeIds}
                  selectedTool={selectedTool}
                  stageScale={debouncedScale}
                  onShapeClick={handleShapeClickWithPanel} // <-- This now correctly passes the original handler
                  onShapeDragEnd={handleShapeDragEnd}
                  onVertexClick={handleVertexClickWithTransform} // Pass the new handler
                  onTransformEnd={handleTransformEnd}
                  onUpdateShape={handleUpdateShape} // Pass update handler for text editing
                  onStartTextEdit={handleStartTextEdit} // Pass text edit starter
                  textEditingShapeId={textEditingState?.shapeId} // Pass which shape is being edited
                  currentScaleProps={currentScaleProps}
                  dpi={dynamicDPI} // Pass the calculated DPI
                  imageSize={imageSize}
                  isDraggingMode={isDraggingMode}
                  vertexEditInfo={vertexEditInfo} // Pass vertex edit state
                  stageRef={stageRef} // Pass stageRef for text editing
                  groupingKey={groupingKey} // Pass groupingKey for param display
                  displayKeys={displayKeys} // Pass displayKeys for multi-param text display
                  displayUnitSystem={displayUnitSystem}
                  showOverlay={overlayEnabled}
                  allShapes={shapes} // Pass all shapes for measurement summary
                  onShapeSelect={handleShapeListItemSelect} // Pass selection handler for measurement summary
                  onShapeHover={handleShapeHover} // Handle hover cursor state
                  allScales={allScales} // Pass all scales for locked scale indicator
                  hoveredSegmentInfo={hoveredSegmentInfo && hoveredSegmentInfo.shapeId === shape.id ? hoveredSegmentInfo : null}
                />
              ))}
            {/* Render the current shape being drawn (preview) */}
            {isDrawing && currentShape && (
              <ShapeRenderer
                key={`preview-${currentShape.id}`}
                shape={currentShape}
                isSelected={false} // Preview is not "selected" for transformation
                isDrawingPreview={true} // Enable AutoCAD guides during drawing
                selectedShapeIds={[]} // Empty array so preview shape text/measurements are always shown during drawing
                selectedTool={selectedTool}
                stageScale={safeStageScale}
                // Event handlers are typically not needed for a preview
                onShapeClick={() => { }}
                onShapeDragEnd={() => { }}
                onTransformEnd={() => { }}
                onUpdateShape={() => { }} // Empty handler for preview
                onStartTextEdit={() => { }} // Empty handler for preview
                textEditingShapeId={null} // No editing in preview
                stageRef={stageRef}
                currentScaleProps={currentScaleProps}
                dpi={dynamicDPI} // Pass the calculated DPI to preview as well
                imageSize={imageSize}
                isDraggingMode={isDraggingMode}
                groupingKey={groupingKey} // Pass groupingKey for param display
                displayKeys={[DISPLAY_KEY_MEASUREMENT, DISPLAY_KEY_SEGMENT_LENGTH, DISPLAY_KEY_AREA]} // Always show measurements while drawing
                displayUnitSystem={displayUnitSystem}
                showOverlay={overlayEnabled}
                allScales={allScales}
              />
            )}
            {/* Render the selection rectangle for area select */}
            {selectionRect && selectionRect.visible && (
              <KonvaRect
                x={selectionRect.x}
                y={selectionRect.y}
                width={selectionRect.width}
                height={selectionRect.height}
                fill="rgba(0, 168, 255, 0.2)" // Light blue semi-transparent fill
                stroke="#00A8FF" // Blue border
                strokeWidth={1 / safeStageScale} // Thin border, adjusted for scale
              />
            )}
            {/* Render visible snap points within SNAP_VISIBILITY_THRESHOLD */}
            {visibleSnapPoints && visibleSnapPoints.length > 0 && visibleSnapPoints.map((point, idx) => (
              <Circle
                key={`snap-${idx}`}
                x={point.x}
                y={point.y}
                radius={3 / safeStageScale}
                fill="rgba(0, 168, 255, 0.15)"
                stroke="rgba(0, 168, 255, 0.3)"
                strokeWidth={1 / safeStageScale}
                listening={false}
                perfectDrawEnabled={false}
              />
            ))}
            {/* Render the snap indicator when not drawing */}
            {snapIndicator && snapIndicator.visible && (
              <Circle
                x={snapIndicator.x}
                y={snapIndicator.y}
                radius={4 / safeStageScale}
                fill="rgba(0, 168, 255, 0.2)"
                stroke="#00A8FF"
                strokeWidth={2 / safeStageScale}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}

            {/* Transform Preview Group - ghost shapes and guide lines */}
            {transformPreview && (
              <Group name="transform-preview-group" listening={false}>
                {/* Ghost shape outlines */}
                {transformPreview.ghostShapes?.map((ghost, i) => (
                  <KonvaLine
                    key={`ghost-${i}`}
                    points={ghost.points}
                    stroke={ghost.stroke || '#00A8FF'}
                    strokeWidth={2 / safeStageScale}
                    closed={ghost.closed}
                    opacity={0.8}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                ))}
                {/* Guide line (axis for symmetry, radius for rotate/scale) */}
                {transformPreview.line && (
                  <>
                    <KonvaLine
                      points={[
                        transformPreview.line.from.x,
                        transformPreview.line.from.y,
                        transformPreview.line.to.x,
                        transformPreview.line.to.y,
                      ]}
                      stroke={selectionColor}
                      strokeWidth={1 / safeStageScale}
                      dash={[8 / safeStageScale, 4 / safeStageScale]}
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                    <SegmentMeasurementOverlay
                      x1={transformPreview.line.from.x}
                      y1={transformPreview.line.from.y}
                      x2={transformPreview.line.to.x}
                      y2={transformPreview.line.to.y}
                      activeStrokeColor={selectionColor}
                      stageScale={safeStageScale}
                      textFontSize={Math.max(10, Math.min(50, 15 / (0.95 * safeStageScale)))}
                      shapeScaleProps={currentScaleProps}
                      dpi={dynamicDPI}
                      imageSize={imageSize}
                      displayUnitSystem={displayUnitSystem}
                      showOverlay={overlayEnabled}
                    />
                  </>
                )}
                {/* Pivot point marker */}
                {transformPreview.pivot && (
                  <Circle
                    x={transformPreview.pivot.x}
                    y={transformPreview.pivot.y}
                    radius={5 / safeStageScale}
                    fill={selectionColor}
                    stroke="#fff"
                    strokeWidth={1.5 / safeStageScale}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                )}
                {/* Snap indicator */}
                {transformSnapIndicator && (
                  <Circle
                    x={transformSnapIndicator.x}
                    y={transformSnapIndicator.y}
                    radius={6 / safeStageScale}
                    fill="transparent"
                    stroke="#00FF88"
                    strokeWidth={2 / safeStageScale}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                )}
              </Group>
            )}

            {/* Page Reference Highlights Group */}
            {/* Renders blue clickable rectangles over text that references other pages */}
            {/* Clicking navigates to the referenced page */}
            <Group name="page-reference-group">
              {pageReferences.map((ref, index) => (
                <KonvaRect
                  key={`page-ref-${index}`}
                  x={ref.position.x}
                  y={ref.position.y + 1.75}
                  width={ref.position.width}
                  height={ref.position.height}
                  fill="rgba(0, 123, 255, 0.25)"
                  strokeWidth={0}
                  onClick={(e) => handleNavigateToPageReference(ref.targetPage, ref, e)}
                  onTap={(e) => handleNavigateToPageReference(ref.targetPage, ref, e)}
                  onMouseEnter={(e) => handleReferenceMouseEnter(ref, e)}
                  onMouseMove={handleReferenceMouseMove}
                  onMouseLeave={handleReferenceMouseLeave}
                />
              ))}
            </Group>

            {/* OCR Search Highlights Group */}
            {/* Renders yellow rectangles over OCR text matches */}
            {/* Current match highlighted in orange, others in yellow */}
            <Group name="search-group" listening={false}>
              {searchMatches.map((match, index) => {
                const isCurrentMatch = index === currentSearchIndex;
                // Extract position from the match object (OCR API returns {position: {x, y, width, height}})
                const pos = match.position || match;
                return (
                  <KonvaRect
                    key={`search-${index}`}
                    x={pos.x}
                    y={pos.y + 1.75}
                    width={pos.width}
                    height={pos.height}
                    fill={
                      isCurrentMatch
                        ? 'rgba(255, 165, 0, 0.4)'
                        : 'rgba(255, 255, 0, 0.3)'
                    }
                    stroke={isCurrentMatch ? '#FF8C00' : '#FFD700'}
                    strokeWidth={
                      isCurrentMatch
                        ? 2 / safeStageScale
                        : 1 / safeStageScale
                    }
                    listening={false}
                  />
                );
              })}
            </Group>
          </Layer>
        </Stage>
      )}

            {/* Tooltip for the empty-sheet links */}
            {emptyTooltip && (
              <div
                className="replan-empty-tooltip"
                style={{ left: emptyTooltip.x, top: emptyTooltip.y }}
              >
                {emptyTooltip.text}
              </div>
            )}


      {/* Text Editor for rectangles - rendered outside Konva Stage */}
      {textEditingState &&
        textEditingState.textRef &&
        (() => {
          const editingShape = deduplicatedShapes.find(
            (s) => s.id === textEditingState.shapeId,
          );
          return (
            <TextEditor
              textNode={textEditingState.textRef}
              stageRef={stageRef}
              onChange={handleTextChange}
              onClose={() => setTextEditingState(null)}
              initialText={editingShape?.text}
            />
          );
        })()}

      {/* Standalone Display Keys Toggles Panel */}
      {konvaImage && deduplicatedShapes.length > 0 && displayKeysPanelVisible && (
        <DisplayKeysPanel
          displayKeys={displayKeys}
          onDisplayKeysChange={setDisplayKeys}
          measurementSystem={measurementSystem}
          displayUnitSystem={displayUnitSystem}
          onDisplayUnitSystemChange={setDisplayUnitSystem}
          allParamKeys={allParamKeys}
          shapeListVisible={shapeListVisible}
          overlayEnabled={overlayEnabled}
          onToggleOverlay={() => setOverlayEnabled(!overlayEnabled)}
          contextualPanelVisible={contextualPanelVisible}
        />
      )}

      {/* Shape List Area */}
      {(konvaImage 
      // && deduplicatedShapes.length > 0
      ) && (
          <ShapeList
            projectName={projectName}
            organization={currentPageData?.organization}
            shapes={deduplicatedShapes}
            selectedShapeIds={selectedShapeIds}
            onShapeSelect={handleShapeListItemSelect}
            onUpdateShape={handleUpdateShape}
            currentScaleProps={currentScaleProps}
            dpi={dynamicDPI}
            imageSize={imageSize}
            allScales={allScales}
            authUser={authUser}
            firebase={firebase}
            groupingKey={groupingKey}
            onGroupingKeyChange={setGroupingKey}
            displayKeys={displayKeys}
            onDisplayKeysChange={setDisplayKeys}
            measurementSystem={measurementSystem}
            displayUnitSystem={displayUnitSystem}
            onDisplayUnitSystemChange={setDisplayUnitSystem}
            visible={shapeListVisible}
            onVisibilityChange={setShapeListVisible}
            displayKeysPanelVisible={displayKeysPanelVisible}
            onToggleDisplayKeysPanel={() => setDisplayKeysPanelVisible(!displayKeysPanelVisible)}
            onCopyForeignShape={handleCopyForeignShape}
            onDrawSameShape={handleDrawSameShape}
            onRestoreDrawSettings={handleRestoreDrawSettings}
            selectedTool={selectedTool}
          />
        )}
      {/* Page Reference Thumbnail Preview */}
      <HoverThumbnailPreview
        targetPageLabel={previewData?.targetPageLabel}
        position={previewData?.position || { x: 0, y: 0 }}
        pages={pages}
      />
    </div>
  );
};

export default withEmailVerification(
  withAuthorization(condition)(withFirebase(Replan)),
);
